// 11 §4 — the encounter scene. Two cameras (board: eased, free-flowing zoom and pan, docs/33 E5–E6 and docs/42;
// overlay: CSS px at the device pixel ratio, docs/33 E4),
// layers bottom-to-top: terrain, light, props, shadows, low, fog, edge, units, high, effects (board
// camera; the lighting layers are docs/19, the fog of war docs/31); declarations, intent, hud, pings (overlay camera).
// The scene never reads server rows: it draws the board the encounter controller publishes
// (`useEncounter.boardSnapshot`), subscribes to `AuthoringStore.projected` and redraws (§4.3),
// and animates `resolution_log`s through the PlaybackStore. RouteStore drives entry/exit.
import { registerKitTextures } from '../board/heroTextures';
import { playItemSfx } from '../../core/items/sound';
import { blowGlint, mainHandOf } from '../../core/items/worn';
import { instrumentOf } from '../../core/sim/instrument';
import Phaser from 'phaser';
import type { GameBoot } from '../Game';
import { BoardMapper, centredScroll, clampScroll, clampZoom, EdgePan, edgePush, fitStop, pixelRatio, stepZoom, wheelZoomFactor, ZOOM_KEY_RATE } from '../board/BoardCamera';
import { TileRenderer } from '../board/TileRenderer';
import { ObjectiveGlow } from '../board/ObjectiveGlow';
import { StashLayer } from '../board/StashLayer';
import { FixtureLayer } from '../board/FixtureLayer';
import { MatterLayer } from '../matter/MatterLayer';
import { matterLights, matterRow, reactLook } from '../../core/matter/matter';
import { SecretLayer } from '../board/SecretLayer';
import { secretName } from '../../core/sim/secrets';
import { hintReveal } from '../../core/puzzle/reveal';
import { usePuzzle } from '../../core/stores/puzzle';
import { facingAfter, type Facing } from '../../core/playback/facing';
import { UnitSprite } from '../board/UnitSprite';
import { thumpShake } from '../../core/playback/walk';
import { sizeOf, tilesAt } from '../../core/sim/body';
import { EffectPlayer, type EffectPlayerHost } from '../board/EffectPlayer';
import { SpellFx, type SpellQuality } from '../spells/SpellFx';
import { CombatFx } from '../board/CombatFx';
import { FlightFx } from '../board/FlightFx';
import { WaterFx } from '../water/WaterFx';
import { fieldMoves, gaitKey, swims } from '../../core/sim/water';
import { TentacleFx } from '../board/TentacleFx';
import { SETTLE_MS } from '../../core/playback/catalogue';
import { foesFrom, onlyWalked } from '../../core/playback/plan';
import { LiveBoard } from '../../core/playback/live';
import { playSfx } from '../../core/audio';
import { cancelPulseCues, playAfterPlayback, playEntryCues } from '../../core/audio/cues';
import { panForTile } from '../../core/audio/mix';
import { hugeSpace } from '../../core/audio/hall';
import { hugeEyebrow, hugeLine, hugeTitle, markRevealed, revealedBefore, useHuge, type HugeSay } from '../../core/huge/voices';
import { HUGE_REVEAL, HUGE_TAUNT, momentOf } from '../huge/moments';
import { faceOf } from '../hugeArt';
import { ambienceFor, setAmbience } from '../../core/audio/ambience';
import { playEscape } from '../board/escape';
import { CorpseLayer } from '../board/CorpseLayer';
import { IntentLayer, type IntentView } from '../intent/IntentLayer';
import { playerSeats, seatColour } from '../board/players';
import { FogLayer, closedDoorsField, unitChanges, type FogEyeColour } from '../fog';
import { WildlifeLayer } from '../wildlife';
import { wildFeed } from '../../core/wildlife/feed';
import { WILDLIFE_ENABLED } from '../../core/features';
import { LightingLayer, climateFor, encounterHour, type LightingLayers } from '../lighting';
import { encounterWeather, parseSeason, parseWeather, weatherLabel } from '../weather';
import { useAuthoring } from '../../core/stores/authoring';
import { useView } from '../../core/stores/view';
import { useSettings } from '../../core/stores/settings';
import { usePlayback } from '../../core/stores/playback';
import { isNamedFoe, useCinematic } from '../../core/stores/cinematic';
import type { Escape } from '../../core/story/genesis';
import { getBridge, playbackHandles, useEncounter } from '../../core/encounter/session';
import { useBoardMenu } from '../../core/encounter/pings';
import { serverNow } from '../../core/time';
import { motionMs } from '../../ui/components';
import { streakKillAt } from '../../core/encounter/streak';
import { StreakFx } from '../board/StreakFx';
import { EmoteLayer } from '../board/EmoteLayer';
import { useSession } from '../../core/stores/session';
import { getConn } from '../../core/net/connection';
import type { Intent, ResolutionEntry, Slot, Snapshot, Tile } from '../../core/sim/types';
import { blindFog, onBoard as squareOnBoard, tileEq, unknownSquare } from '../../core/sim/types';
import { VoidLayer } from '../board/VoidLayer';
import { fixtureVerb, reasonText } from '../../core/authoring/stateMachine';
import { MOVE, type ActionDef, type Glyph } from '../../core/sim/actions';
import { beatCues } from '../../core/story/cues';
import { BOON_WORDS, crawlCallout, partyFrame, storyCallout, type Callout } from '../../core/crawl/crawl';
import { followsJump } from '../../core/crawl/floors';
import { useCrawl } from '../../core/stores/crawl';
import { ExploreLayer } from '../board/ExploreLayer';
import { exploreCallout, type ExploreCallout } from '../../core/explore/explore';
import { useExplore } from '../../core/stores/explore';
import { CrawlLayer } from '../board/CrawlLayer';
import { SnareFx } from '../board/SnareFx';
import { SnareLayer } from '../board/SnareLayer';
import { snareCallout, snareOf, snarePresentation, underfoot } from '../../core/snares/snares';
import { fireBeat } from '../../ui/components/StoryFx';
import { CallLayer } from '../board/CallLayer';
import { squareTarget, unitTarget, type SeeTarget } from '../encounter/seeThrough';
import { callBanner } from '../../core/encounter/calls';
import { CAMERA_HOLD_IDS, CAMERA_PAN, CAMERA_ZOOM, hotkeyFor } from '../../core/input/hotkeys';
import { killTweens, oneShot } from '../board/oneShots';

/** Beat `i` of the story's battle is event card `BEAT_CARD + i` (fk-forge `set_piece::BEAT_CARD`). */
const BEAT_CARD = 100;

/** docs/42 CM5: the edge band (CSS px from the window's edge) and what an edge pan never starts over. */
const EDGE_BAND = 12;
const EDGE_BLOCKERS = 'button, a, input, select, textarea, label, summary, [role="button"], [role="slider"], [role="tab"], [role="menu"], [role="menuitem"], [role="dialog"], [role="listbox"], .fk-hit';
/** docs/42 CM4: easing time constants (s) — zoom, pinch, held-key and edge pans, drags; the face-off is slower. */
const EASE = { zoom: 0.1, pinch: 0.045, pan: 0.07, drag: 0.035, slow: 0.32 };
const REACH_TONE: Partial<Record<Glyph, 'move' | 'attack' | 'area' | 'support'>> = { move: 'move', attack: 'attack', beam: 'attack', area: 'area', heal: 'support', ward: 'support', shield: 'support', guard: 'support', interact: 'support' };

export class Encounter extends Phaser.Scene {
  private boardCam!: Phaser.Cameras.Scene2D.Camera;
  private overlayCam!: Phaser.Cameras.Scene2D.Camera;
  private mapper!: BoardMapper;
  private layers!: { terrain: Phaser.GameObjects.Layer; lit: Phaser.GameObjects.Layer; props: Phaser.GameObjects.Layer; fog: Phaser.GameObjects.Layer; edge: Phaser.GameObjects.Layer; marks: Phaser.GameObjects.Layer; units: Phaser.GameObjects.Layer; effects: Phaser.GameObjects.Layer } & LightingLayers;
  private overlay!: { declarations: Phaser.GameObjects.Container; intent: Phaser.GameObjects.Container; hud: Phaser.GameObjects.Container; pings: Phaser.GameObjects.Container };
  /** Screen-fixed (CSS px): banners. */
  private screenLayer!: Phaser.GameObjects.Container;
  private tiles!: TileRenderer;
  private stashes!: StashLayer;
  /** docs/130 OB3: the squares (and the ally) the stakes are about, shimmering gold. */
  private objectives!: ObjectiveGlow;
  private fixtures!: FixtureLayer;
  /** docs/114: spills, clouds, flames and the things on the field. */
  private matterFx!: MatterLayer;
  /** docs/115: the interaction rows played on this board, in order (for `__fkGround`). */
  private reacted: number[] = [];
  /** docs/114: when each interaction row's words last rose (scene time). */
  private readonly saidAt = new Map<number, number>();
  /** docs/52: found hidden things and lair traps (disguised until seen through). */
  private hidden!: SecretLayer;
  /** docs/66 RM10: a pending call's arrival squares and a raising's bodies. */
  private calls!: CallLayer;
  /** docs/60: a crawl's lights (sconces, braziers, candles, glowing moss, ember cracks). */
  private crawlProps!: CrawlLayer;
  /** docs/140: found plates and wires, blades on their lanes, cages and nets, rollers' looks. */
  private snareProps!: SnareLayer;
  /** docs/140 DT14: every trap's moment drawn as it plays. */
  private snareFx!: SnareFx;
  /** docs/140: the trap the camera went to (released when it is spent, or after a while). */
  private snareFocus?: { snare: number; tile: Tile };
  /** The trap moments played this scene (drivers read them through `__fkSnareMoments`). */
  readonly snareMoments: Array<{ kind: string; snare: number; act?: string; at: number }> = [];
  /** docs/61: an exploration's wonders, veils, ways and air. */
  private explore!: ExploreLayer;
  private edge!: VoidLayer;
  private corpses!: CorpseLayer;
  private intent!: IntentLayer;
  private player!: EffectPlayer;
  private spells!: SpellFx;
  private combat!: CombatFx;
  private flight!: FlightFx;
  /** docs/93 AQ10: wakes, foam, splashes and the withdrawing swirl. */
  private water!: WaterFx;
  private tentacles!: TentacleFx;
  private streak!: StreakFx;
  private units = new Map<number, UnitSprite>();
  /** Which way each figure faces, as the last pulse's actions left it (`core/playback/facing`), for this encounter. */
  private facing = new Map<number, Facing>();
  private facingFor?: number;
  private snapshot?: Snapshot;
  private cell = 24;
  private dashPhase = 0;
  private unsubs: Array<() => void> = [];
  private lastPlan: unknown;
  /** docs/56: the board part-way through the pulse being played. */
  private live?: LiveBoard;
  private manifest!: GameBoot['manifest'];
  private reachCache?: { key: string; reach: IntentView['reach'] };
  private fieldKey = '';
  private lighting!: LightingLayer;
  private lightKey = '';
  private fog!: FogLayer;
  private wild!: WildlifeLayer;
  /** docs/62: the heroes' emote bubbles (screen-fixed, following their units). */
  private emotes!: EmoteLayer;
  /** docs/22 O4: the face-off has the camera (framing the leader); the glyph layers stand down. */
  private focused = false;
  // docs/33 E5–E6: the camera. `cam` is what is shown (CSS zoom, world scroll), `goal` where it is
  // easing to; `anchor` pins a world point under a screen point while the zoom eases. The overlay
  // glyphs were drawn at `drawnZoom` and are scaled until the zoom settles and they are redrawn.
  private dpr = 1;
  private cam = { zoom: 3, x: 0, y: 0 };
  private goal = { zoom: 3, x: 0, y: 0 };
  private anchor?: { sx: number; sy: number; wx: number; wy: number };
  private drawnZoom = 3;
  private fitted = false;
  private slow = false;
  private preFocus?: { zoom: number; x: number; y: number };
  /** docs/87: held camera hotkeys, by the physical key holding each (key code → hotkey id). */
  private held = new Map<string, string>();
  private press?: { sx: number; sy: number; x: number; y: number; moved: boolean; button: number; trail: Array<{ t: number; sx: number; sy: number }> };
  /** docs/42 CM4: two fingers on the canvas (CSS px), and the pinch they make: the world point under their midpoint and the zoom it began at. */
  private touches = new Map<number, { x: number; y: number }>();
  private pinch?: { d0: number; z0: number; wx: number; wy: number };
  /** docs/42 CM4: Safari's trackpad pinch (gesture events), as a zoom it began at. */
  private gesture?: { z0: number };
  /** docs/42 CM5: where the mouse is in the window (CSS px) and whether it may edge-pan from there. */
  private edgeAt?: { x: number; y: number; ok: boolean };
  /** The pointer is over a board square: with no hero selected the cursor and its chip show only then. */
  private hovering = false;
  /** docs/186 EA12: the square under the local mouse (its obscuring pieces fade, on this client only). */
  private seeHover?: Tile;
  private edgePan = new EdgePan();
  private easing: keyof typeof EASE = 'pan';

  constructor() { super('Encounter'); }

  create(): void {
    // docs/145 KH6: heroes' kit looks as textures, swapped onto any hero drawn before its look baked
    this.unsubs.push(registerKitTextures(this, () => { for (const u of this.units.values()) u.useKit(this.manifest); }));
    // Phaser keeps this instance between runs (Boot stops the scene off the encounter route and
    // launches it again): start every run from no board and the default camera, so a relaunch fits
    // and draws the room like the first run did (a kept `snapshot` of the same size skipped the fit)
    this.snapshot = undefined; this.fitted = false; this.focused = false; this.preFocus = undefined;
    this.units.clear(); this.held.clear(); this.touches.clear();
    this.lightKey = ''; this.reachCache = undefined; this.hugeBloodied.clear();
    this.cam = { zoom: 3, x: 0, y: 0 }; this.goal = { zoom: 3, x: 0, y: 0 }; this.drawnZoom = 3;
    this.anchor = undefined; this.press = undefined; this.pinch = undefined; this.gesture = undefined;
    this.edgeAt = undefined; this.edgePan = new EdgePan(); this.easing = 'pan';
    const boot = this.registry.get('boot') as GameBoot;
    this.manifest = boot.manifest;
    this.cell = this.manifest.cell;

    // Cameras (§4.1)
    this.dpr = pixelRatio();
    this.boardCam = this.cameras.main;
    this.boardCam.roundPixels = true;
    this.boardCam.setOrigin(0, 0);
    this.overlayCam = this.cameras.add(0, 0, this.scale.width, this.scale.height, false, 'overlay');
    this.overlayCam.setOrigin(0, 0).setZoom(this.dpr).setScroll(0, 0);
    this.mapper = new BoardMapper(this.boardCam, this.cell, () => this.cam.zoom);

    // Layers (§4.2)
    // creation order is draw order: the light sits between the tiles and the gameplay marks (docs/19 N5)
    // `lit`: the commissioned art's lit pixels, kept bright over the light map at night (docs/186 EA2)
    const terrain = this.add.layer(), light = this.add.layer(), lit = this.add.layer(), props = this.add.layer();
    // docs/31: the fog of war sits over the ground and under the units (everything the party can't see is
    // already off the board) and under the overlay camera, so moves and aims into the fog stay readable
    // docs/38 BS7: a free-form field's edge covers every layer under the units, so none paints on no board
    // `marks`: the selection and cursor squares, drawn on the ground under the units so a figure stands over them
    const shadows = this.add.layer(), low = this.add.layer(), fog = this.add.layer(), edge = this.add.layer(), marks = this.add.layer(), units = this.add.layer(), high = this.add.layer(), effects = this.add.layer();
    this.layers = { terrain, light, lit, props, shadows, low, fog, edge, marks, units, high, effects };
    this.overlay = { declarations: this.add.container(0, 0), intent: this.add.container(0, 0), hud: this.add.container(0, 0), pings: this.add.container(0, 0) };
    this.screenLayer = this.add.container(0, 0);
    this.overlayCam.ignore(Object.values(this.layers));
    this.boardCam.ignore([this.overlay.declarations, this.overlay.intent, this.overlay.hud, this.overlay.pings, this.screenLayer]);

    // docs/186: the fight's terrain in the commissioned art (its set by kit, the World's biome, season and hour)
    this.tiles = new TileRenderer(this, this.layers.terrain, this.layers.props, this.manifest, {
      lit, units,
      want: (board) => ({ biome: worldBiome(board), season: seasonOverride() ?? parseSeason(board.season), hour: this.hourOf(board), ...(timeOverride() ? { time: timeOverride() } : {}) }),
      onReady: () => this.artLanded(),
    });
    this.emotes = new EmoteLayer(this, this.screenLayer, this.mapper, this.cell);
    this.stashes = new StashLayer(this, this.layers.props, this.cell);
    this.objectives = new ObjectiveGlow(this, this.layers.props, this.cell, useSettings.getState().reduceMotion || motionMs(1) === 0);
    this.fixtures = new FixtureLayer(this, this.layers.props, this.cell, this.dpr);
    this.matterFx = new MatterLayer(this, { low: this.layers.low, props: this.layers.props, high: this.layers.high, effects: this.layers.effects }, this.cell, { reduceMotion: useSettings.getState().reduceMotion || motionMs(1) === 0 });
    this.hidden = new SecretLayer(this, this.layers.props, this.cell, this.layers.terrain);
    this.calls = new CallLayer(this, this.layers.props, this.cell, this.dpr);
    this.crawlProps = new CrawlLayer(this, this.layers.props, this.cell);
    this.snareProps = new SnareLayer(this, this.layers.props, this.layers.high, this.cell);
    (window as unknown as { __fkSnareMoments?: unknown }).__fkSnareMoments = this.snareMoments;
    this.explore = new ExploreLayer(this, this.layers.props, this.layers.high, this.cell, this.dpr);
    this.explore.setReducedMotion(useSettings.getState().reduceMotion || motionMs(1) === 0);
    this.edge = new VoidLayer(this, this.layers.edge, this.cell);
    this.corpses = new CorpseLayer(this, this.layers.low, this.manifest);
    this.intent = new IntentLayer(this, { ...this.overlay, ground: this.layers.marks }, this.manifest);
    this.lighting = new LightingLayer(this, this.layers, this.boardCam, this.cell);
    const st = useSettings.getState();
    this.lighting.setMode(st.lighting, st.reduceMotion, st.lightningFlashes);
    this.tiles.art?.setLighting(this.lighting);
    this.fog = new FogLayer(this, this.layers.fog, this.cell, this.manifest);
    this.fog.setMode(fogMode(st));
    // docs/35: the room's creatures, sorted with the units, lit, fogged and hidden behind trees
    this.wild = new WildlifeLayer(this, this.layers.units, this.layers.shadows, this.cell, {
      light: (x, y) => this.lighting.lightAt(x, y), sees: (x, y) => this.fog.sees(x, y), sunny: () => this.lighting.sunny,
    });
    this.wild.setMode(WILDLIFE_ENABLED && st.wildlife, st.reduceMotion);
    // docs/47: spells, drawn in the effects layer over the light map
    this.spells = new SpellFx({ scene: this, add: (o) => { this.layers.effects.add(o); }, addGround: (o) => { this.layers.low.add(o); }, cell: this.cell, tileCentre: (t) => this.mapper.tileCentre(t), unit: (id) => this.units.get(id), camera: this.boardCam,
      boardSize: () => (this.snapshot ? { w: this.snapshot.board.w * this.cell, h: this.snapshot.board.h * this.cell } : undefined) }, spellQuality(st));
    // docs/56 EP4: shots and blood, in the same layers
    this.combat = new CombatFx({ scene: this, add: (o) => { this.layers.effects.add(o); }, addGround: (o) => { this.layers.low.add(o); }, cell: this.cell }, spellQuality(st));
    // docs/102: a flight's shadow, dust and air
    // docs/93 AQ10: wakes, foam and splashes, on the ground under the figures
    this.water = new WaterFx({ scene: this, add: (o) => { this.layers.effects.add(o); }, addGround: (o) => { this.layers.low.add(o); }, cell: this.cell }, spellQuality(st));
    this.flight = new FlightFx({ scene: this, add: (o) => { this.layers.effects.add(o); }, addGround: (o) => { this.layers.low.add(o); }, addShadow: (o) => { this.layers.shadows.add(o); }, cell: this.cell }, spellQuality(st));
    // docs/117 TT6: the Thing Below's arms rising out of the water (or the sand), and swaying
    this.tentacles = new TentacleFx({ scene: this, add: (o) => { this.layers.effects.add(o); }, addGround: (o) => { this.layers.low.add(o); }, cell: this.cell,
      shake: (ms, intensity) => { if (!(useSettings.getState().reduceMotion || motionMs(1) === 0)) this.boardCam.shake(ms, intensity); }, pan: (u) => this.panOf(u) }, spellQuality(st));
    // docs/140 DT14: dungeon traps' moments, over the board (the ground's part under the figures)
    this.snareFx = new SnareFx({ scene: this, hideThings: (t) => this.matterFx.hideThingsOn(t), add: (o) => { this.layers.effects.add(o); }, addGround: (o) => { this.layers.low.add(o); }, cell: this.cell, unit: (id) => this.units.get(id),
      shake: (ms, intensity) => { if (!(useSettings.getState().reduceMotion || motionMs(1) === 0)) this.boardCam.shake(ms, intensity); },
      sees: (t) => this.partySees(t),
      boardSize: () => (this.snapshot ? { w: this.snapshot.board.w * this.cell, h: this.snapshot.board.h * this.cell } : { w: 0, h: 0 }),
      floatText: (t, text, colour, lift) => this.floatText(t, text, colour, lift),
      sound: (key, unit) => { if (snarePresentation.localSounds) playSfx(key, { pan: this.panOf(unit) }); },
      reduced: () => useSettings.getState().reduceMotion || motionMs(1) === 0 });
    // docs/101: my kill streak's call-outs (this client only)
    this.streak = new StreakFx({ scene: this, cell: this.cell, dpr: () => this.dpr, addEffect: (o) => { this.layers.effects.add(o); }, addOverlay: (o) => { this.overlay.pings.add(o); },
      tileCentre: (t) => this.mapper.tileCentre(t), zoom: () => this.drawnZoom, motionMs });
    this.player = new EffectPlayer(this, {
      unit: (id) => this.units.get(id),
      tileCentre: (t) => this.mapper.tileCentre(t),
      cell: this.cell,
      floatText: (t, text, colour, lift) => this.floatText(t, text, colour, lift),
      // docs/41: a block slides, a chest opens, a key is taken up or set as the pulse plays; the
      // next snapshot redraws the room the same
      // docs/114 MT9: the ground reacts — its look over the squares it touched, its words above it
      react: (e, ms) => {
        this.matterFx.react(reactLook(e.rule), e.at, e.tiles, ms);
        this.reacted.push(e.rule);
        // a pool catching square by square is one row firing many times: its words rise once
        const row = matterRow(e.rule);
        const now = this.time.now;
        if (row && (this.saidAt.get(e.rule) ?? -1e9) < now - 1500) {
          this.saidAt.set(e.rule, now);
          this.floatText(e.at, row.say.replace(/[.!]$/, ''), '#ffd9a0', 0.4);
        }
      },
      mechanism: (e, ms, actor) => {
        if (e.moved) this.fixtures.slide(e.fixture, e.moved.to, ms);
        if (e.opened) { this.stashes.open(e.fixture); usePlayback.getState().markOpened(e.fixture); }
        if (e.taken && actor !== undefined) this.fixtures.take(e.fixture, actor);
        if (e.placed !== undefined) this.fixtures.place(e.fixture, e.placed);
      },
      // docs/52: a glint where a hidden thing showed itself, a red snap where a trap sprang; the
      // name is read off the board to come (the one drawn now does not carry what was hidden)
      secret: (e, ms) => { if (e.kind === 'Revealed') this.hidden.glint(e.tile, ms); else this.hidden.snap(e.tile, ms); },
      secretName: (id) => {
        const next = useEncounter.getState().snapshot;
        const f = (next?.fixtures ?? []).find((x) => x.id === id) ?? (this.snapshot?.fixtures ?? []).find((x) => x.id === id);
        if (f) return secretName(f);
        return (next?.stashes ?? []).find((x) => x.id === id)?.name;
      },
      // an event card's own name when the room's deck has one (the log only carries its id)
      banner: (text, ms) => {
        const m = /^Event (\d+)$/.exec(text);
        const t = (m && this.snapshot?.eventNames?.[Number(m[1])]) || (m ? 'Something stirs' : text);
        this.banner(t, t.includes(' — ') ? Math.max(ms, 2600) : ms);
        // docs/50: a battle beat sounds (and shows) as its line appears
        const card = m ? Number(m[1]) : -1;
        const line = t.split(' — ')[1];
        if (card >= BEAT_CARD && line) beatCues(useCinematic.getState().story, card - BEAT_CARD, line).forEach((b, i) => (i ? window.setTimeout(() => fireBeat(b), 260 * i) : fireBeat(b)));
      },
      escape: (id) => this.escapeOf(id),
      // (a getaway's motes, rings and pools are one-shots: they go if a new board kills their tweens)
      escapeHost: (speed) => ({ scene: this, add: (o) => { this.layers.effects.add(oneShot(this, o)); }, cell: this.cell, boardW: this.snapshot?.board.w ?? 0, speed, reduced: useSettings.getState().reduceMotion || motionMs(1) === 0 }),
      // docs/34: the server's cues for each clip land on its beats, placed by the unit's square
      cues: (clip, beats, speed) => {
        const cur = usePlayback.getState().current;
        if (cur) playEntryCues(cur.encounterId, cur.pulseIndex, clip.entry.seq, beats, speed, (u) => this.panOf(u), (u) => this.spaceOf(u));
      },
      cancelCues: () => { const cur = usePlayback.getState().current; if (cur) cancelPulseCues(cur.encounterId, cur.pulseIndex); },
      spell: (cast, ms, impactMs, speed) => {
        // docs/72 IT19: an item's cast sounds its own `sfx.use` at its own variation (a client cue:
        // the director scores hero spells, not the catalogue's pieces)
        if (cast.itemDef !== undefined) playItemSfx(cast.itemDef, 'use');
        // docs/104 BD4: a bard's notes take the colour of the instrument it plays
        if (cast.look.element === 'music' && cast.instrument === undefined) {
          const inst = instrumentOf(this.units.get(cast.caster)?.unit.heroId);
          if (inst !== undefined) cast = { ...cast, instrument: inst };
        }
        return this.spells.play(cast, ms, impactMs, speed);
      },
      blow: (actor, target) => {
        // docs/72 IT18–IT19: a magic weapon's blow glints in its element and strikes with its own sound
        const heroId = this.units.get(actor)?.unit.heroId;
        const weapon = mainHandOf(heroId);
        const g = blowGlint(weapon);
        if (!g) return;
        this.spells.glint(target, g.element, g.accent);
        playItemSfx(weapon, 'strike', { gain: 0.8 });
      },
      // (on a skip the tweens were just killed: the cards still up get their fades back, as on a new board)
      clearSpells: () => { this.spells.clear(); this.snareFx.clear(); this.rearmBanners(); },
      // docs/140: a dungeon trap's moment
      snare: (e, ms) => this.snareMoment(e, ms),
      combat: this.combat,
      finale: (id, ms) => this.finale(id, ms),
      landed: (entries) => this.landed(entries),
      // docs/60: the crawl's banners and call-outs, read off the board to come (it names what the party just found)
      crawl: (e, ms) => this.crawlMoment(e, ms),
      // docs/61: exploration's cards and the board's part of them
      explore: (e, ms) => this.exploreMoment(e, ms),
      // docs/66 RM10: the leader's call, spoken over it and on a banner
      call: (e, ms) => this.callMoment(e, ms),
      searched: (e, ms) => this.searchSweep(e.unit, e.tiles, ms),
      pickedUp: (id) => this.stashes.open(id),
      // docs/138 XO7: a find (a hidden one picked up unseen is in neither board's stashes: on an exploration it is a find)
      isFind: (id) => {
        const s = [...(this.snapshot?.stashes ?? []), ...(useEncounter.getState().snapshot?.stashes ?? [])].find((x) => x.id === id);
        return s ? s.kind === 'Find' : !!this.snapshot?.explore;
      },
      mine: (unit) => {
        const heroId = [...(this.snapshot?.units ?? [])].find((u) => u.unitId === unit)?.heroId;
        return heroId !== undefined && useEncounter.getState().owners[heroId] === useEncounter.getState().me;
      },
      blessed: (unit, ms) => { const u = this.units.get(unit); if (u) this.explore.bless(u.tile, ms); },
      jumped: (unit, from, to, ms) => this.jumped(unit, from, to, ms),
      thump: (size, unit) => this.thump(size, unit),
      huge: (unit, name, ms) => this.hugeMove(unit, name, ms),
      flight: this.flight,
      water: this.water,
      heroUnit: (heroId) => [...this.units.values()].find((u) => u.unit.heroId === heroId),
      panOf: (unit) => this.panOf(unit),
      // docs/101 KS4: a foe of mine falls — the announcer calls the streak, over the one who felled it
      dropped: (entry) => {
        const cur = usePlayback.getState().current;
        const kill = cur ? streakKillAt(cur.pulseIndex, entry.seq) : undefined;
        if (!kill) return;
        playSfx(kill.call.sfx);
        const u = this.units.get(kill.killer);
        if (u) this.streak.celebrate(kill, u.tile, u.size);
      },
    });

    // The board the controller publishes (lags the authoring snapshot during playback, §9.2).
    const s0 = useEncounter.getState().boardSnapshot;
    if (s0) this.setSnapshot(s0);
    this.unsubs.push(useEncounter.subscribe((s, p) => {
      if (s.boardSnapshot && s.boardSnapshot !== p.boardSnapshot) this.setSnapshot(s.boardSnapshot);
    }));

    // Store subscriptions → redraws
    this.unsubs.push(useAuthoring.subscribe((s, p) => {
      if (s.projected !== p.projected || s.machineState !== p.machineState || s.cursor !== p.cursor || s.autoSelected !== p.autoSelected) this.redraw();
      if (s.struck.seq !== p.struck.seq) for (const x of s.struck.items) this.whooshStruck(x.heroId, x.slot);
    }));
    this.unsubs.push(useView.subscribe((s, p) => {
      if (s.viewport !== p.viewport) { if (!this.fitted) this.fitAndCentre(); else this.clampGoal(); }
      // (the scene's own zoom publish carries its goal and no anchor: nothing to do for that)
      if ((s.zoomGoal !== p.zoomGoal || s.zoomAnchor !== p.zoomAnchor) && (s.zoomGoal !== this.goal.zoom || s.zoomAnchor)) { this.easing = 'zoom'; this.zoomTo(s.zoomGoal, s.zoomAnchor); }
      if (s.frame !== p.frame) { if (s.frame.kind === 'fit') this.fitAndCentre(false, true); else if (s.frame.kind === 'unit' && s.frame.unit !== undefined) this.lookAtUnit(s.frame.unit); else this.centreOnHero(); }
      // the scene's own camera publish changes only zoom/scroll: nothing to redraw for that
      if (s.filter !== p.filter || s.hidePaths !== p.hidePaths || s.ownerHighlight !== p.ownerHighlight || s.hoverHero !== p.hoverHero || s.hoverAction !== p.hoverAction || s.humans !== p.humans || s.viewport !== p.viewport) this.redraw();
    }));
    this.unsubs.push(useSettings.subscribe((s) => { this.spells.setQuality(spellQuality(s)); this.combat.setQuality(spellQuality(s)); this.flight.setQuality(spellQuality(s)); this.water.setQuality(spellQuality(s)); this.tentacles.setQuality(spellQuality(s)); this.lighting.setMode(s.lighting, s.reduceMotion, s.lightningFlashes); this.wild.setMode(WILDLIFE_ENABLED && s.wildlife, s.reduceMotion); this.fog.setMode({ ...fogMode(s), wind: this.lighting.weatherNow.wind, weather: this.lighting.weatherNow.kind }); this.redraw(); }));
    // docs/186 EA5: the World clock's biome may arrive after the board: draw the board in its biome's set then
    const clockTable = getConn()?.db.worldClock;
    if (clockTable) {
      let biome = '';
      const onClock = (_ctx: unknown, row: { worldId: bigint; biome: string }) => {
        if (row.worldId !== useSession.getState().worldId || row.biome === biome) return;
        biome = row.biome;
        if (this.snapshot) this.artLanded();
      };
      const onClockUpdate = (ctx: unknown, _old: { worldId: bigint; biome: string }, row: { worldId: bigint; biome: string }) => onClock(ctx, row);
      clockTable.onInsert(onClock); clockTable.onUpdate(onClockUpdate);
      this.unsubs.push(() => { clockTable.removeOnInsert(onClock); clockTable.removeOnUpdate(onClockUpdate); });
    }
    this.unsubs.push(usePlayback.subscribe((s) => { if (s.playing && s.plan && s.plan !== this.lastPlan) { this.lastPlan = s.plan; void this.runPlayback(); } }));
    usePlayback.getState().setHasPlayer(true);
    this.unsubs.push(useCinematic.subscribe((s, p) => { if (s.focus !== p.focus) this.frame(s.focus, s.focusCloser); }));
    // docs/41: the fixture a hint or the puzzle panel points at glows on the board
    this.unsubs.push(usePuzzle.subscribe((s, p) => {
      if ((s.hinted !== p.hinted || s.stepped !== p.stepped) && this.snapshot) { this.drawFixtures(this.snapshot); return; }
      if (s.focus !== p.focus) this.fixtures.focus(s.focus);
    }));

    // Input (board camera is `main`, so pointer.worldX/Y are board pixels)
    // docs/33 E1/E6: a click (press and release without dragging) plans; a drag pans the camera
    // (docs/42 CM4: the camera follows the drag on a short ease and coasts a little after a flick);
    // docs/83 PO9: right-click lets go of the selected hero; with none selected it opens the board's
    // small menu (Ping), the HUD's
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (p: Phaser.Input.Pointer) => {
      const sx = p.x / this.dpr, sy = p.y / this.dpr;
      if (this.pinch) return;
      if (this.press && p.isDown) {
        if (!this.press.moved && Math.hypot(sx - this.press.sx, sy - this.press.sy) > 6) this.press.moved = true;
        if (this.press.moved) {
          this.anchor = undefined; this.easing = 'drag';
          this.goal.x = this.press.x - (sx - this.press.sx) / this.cam.zoom; this.goal.y = this.press.y - (sy - this.press.sy) / this.cam.zoom;
          this.clampGoal();
          const now = p.event?.timeStamp ?? performance.now();
          this.press.trail.push({ t: now, sx, sy });
          while (this.press.trail.length > 2 && now - this.press.trail[0]!.t > 100) this.press.trail.shift();
        }
        return;
      }
      const t = this.tileAtScreen(sx, sy);
      this.setHovering(!!t);
      this.seeHover = t && !p.wasTouch ? t : undefined;
      if (t) useAuthoring.getState().hoverTile(t);
    });
    this.input.on(Phaser.Input.Events.GAME_OUT, () => { this.setHovering(false); this.seeHover = undefined; });
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      const sx = p.x / this.dpr, sy = p.y / this.dpr;
      if (p.rightButtonDown()) {
        const a = useAuthoring.getState();
        if (a.machineState.kind !== 'Idle') { a.send({ type: 'selectHero', heroId: undefined }); return; }
        const t = this.tileAtScreen(sx, sy);
        const c = this.game.canvas.getBoundingClientRect();
        if (t) useBoardMenu.getState().open({ x: c.left + sx, y: c.top + sy, tile: t });
        return;
      }
      if (this.pinch) return;
      // a card over the board goes at a click (it would go on its own; the player has read it)
      if (this.dismissBannerAt(sx, sy)) return;
      this.press = { sx, sy, x: this.goal.x, y: this.goal.y, moved: false, button: p.button, trail: [{ t: p.event?.timeStamp ?? performance.now(), sx, sy }] };
    });
    this.input.on(Phaser.Input.Events.POINTER_UP, (p: Phaser.Input.Pointer) => {
      const press = this.press;
      this.press = undefined;
      if (press?.moved) { this.fling(press.trail, p.event?.timeStamp ?? performance.now()); return; }
      if (!press || press.button !== 0) return;
      const t = this.tileAtScreen(p.x / this.dpr, p.y / this.dpr);
      if (t) useAuthoring.getState().clickTile(t);
    });
    this.input.mouse?.disableContextMenu();
    // docs/42 CM1–CM2: the wheel and a trackpad pinch (ctrl+wheel; Safari's gesture events) zoom
    // smoothly about the pointer; a pinch over the HUD must not zoom the page instead
    const canvas = this.game.canvas;
    canvas.style.touchAction = 'none';
    const local = (cx: number, cy: number) => { const b = canvas.getBoundingClientRect(); return { x: cx - b.left, y: cy - b.top }; };
    const onWheel = (e: WheelEvent) => {
      if (!this.scene.isActive()) return;
      if (e.target !== canvas) { if (e.ctrlKey) e.preventDefault(); return; }
      e.preventDefault();
      if (e.deltaY === 0 || this.gesture) return;
      this.zoomAt(wheelZoomFactor(e), local(e.clientX, e.clientY), 'zoom');
    };
    type GestureEv = Event & { scale: number; clientX: number; clientY: number };
    const onGestureStart = (e: Event) => { e.preventDefault(); if (this.touches.size < 2) this.gesture = { z0: this.goal.zoom }; };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const g = e as GestureEv;
      if (!this.gesture || !this.scene.isActive()) return;
      this.zoomAt(clampZoom(this.gesture.z0 * g.scale) / this.goal.zoom, local(g.clientX, g.clientY), 'pinch');
    };
    const onGestureEnd = (e: Event) => { e.preventDefault(); this.gesture = undefined; };
    // docs/42 CM4: two fingers pinch-zoom about their midpoint and pan with it; a second finger
    // cancels the first one's click or drag
    const mid = () => { const [a, b] = [...this.touches.values()]; return { x: (a!.x + b!.x) / 2, y: (a!.y + b!.y) / 2, d: Math.max(1, Math.hypot(a!.x - b!.x, a!.y - b!.y)) }; };
    const onTouchDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      this.touches.set(e.pointerId, local(e.clientX, e.clientY));
      if (this.touches.size !== 2 || this.focused || !this.snapshot) return;
      this.press = undefined;
      const m = mid();
      this.pinch = { d0: m.d, z0: this.cam.zoom, wx: m.x / this.cam.zoom + this.cam.x, wy: m.y / this.cam.zoom + this.cam.y };
    };
    const onTouchMove = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !this.touches.has(e.pointerId)) return;
      this.touches.set(e.pointerId, local(e.clientX, e.clientY));
      if (!this.pinch || this.touches.size < 2 || this.focused) return;
      const m = mid(), z = clampZoom(this.pinch.z0 * (m.d / this.pinch.d0));
      this.easing = 'pinch';
      this.anchor = { sx: m.x, sy: m.y, wx: this.pinch.wx, wy: this.pinch.wy };
      this.goal = { zoom: z, x: this.pinch.wx - m.x / z, y: this.pinch.wy - m.y / z };
      this.clampGoal();
      this.publishZoom();
    };
    const onTouchUp = (e: PointerEvent) => { this.touches.delete(e.pointerId); if (this.touches.size < 2) this.pinch = undefined; };
    // docs/33 E6: WASD and the arrow keys pan while held (Shift+arrows still walk the tile cursor); docs/42 CM3: Q and E zoom
    // (default keys; docs/87 rebinds them, and a held key needs no modifier)
    const typing = (e: KeyboardEvent) => { const t = e.target as HTMLElement | null; return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable); };
    const onDown = (e: KeyboardEvent) => {
      const id = hotkeyFor(e, CAMERA_HOLD_IDS);
      if (!id || typing(e) || !this.scene.isActive()) return;
      e.preventDefault();
      // a tap of a zoom key is a small nudge at once; holding it keeps zooming (stepCamera)
      if (CAMERA_ZOOM[id] && !this.held.has(e.code)) this.zoomAt(ZOOM_KEY_RATE ** (CAMERA_ZOOM[id]! * 0.12), undefined, 'zoom');
      this.held.set(e.code, id);
    };
    const onUp = (e: KeyboardEvent) => { this.held.delete(e.code); };
    const onBlur = () => { this.held.clear(); this.edgeAt = undefined; };
    // docs/42 CM5: the mouse's place in the window for edge panning (never from over a control, a held button or a touch)
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Element | null;
      this.edgeAt = e.pointerType === 'mouse' && e.buttons === 0 ? { x: e.clientX, y: e.clientY, ok: !t?.closest?.(EDGE_BLOCKERS) } : undefined;
    };
    const onLeave = (e: MouseEvent) => { if (!e.relatedTarget) this.edgeAt = undefined; };
    const on = <K extends keyof WindowEventMap>(k: K, f: (e: WindowEventMap[K]) => void, o?: AddEventListenerOptions) => { window.addEventListener(k, f, o); this.unsubs.push(() => window.removeEventListener(k, f, o)); };
    const onCanvas = (k: string, f: (e: never) => void, o?: AddEventListenerOptions) => { canvas.addEventListener(k, f as EventListener, o); this.unsubs.push(() => canvas.removeEventListener(k, f as EventListener, o)); };
    on('wheel', onWheel, { passive: false });
    on('keydown', onDown); on('keyup', onUp); on('blur', onBlur);
    on('pointermove', onPointer, { passive: true }); on('pointerdown', onPointer, { passive: true });
    document.addEventListener('mouseout', onLeave); this.unsubs.push(() => document.removeEventListener('mouseout', onLeave));
    onCanvas('gesturestart', onGestureStart, { passive: false }); onCanvas('gesturechange', onGestureChange, { passive: false }); onCanvas('gestureend', onGestureEnd, { passive: false });
    onCanvas('pointerdown', onTouchDown); onCanvas('pointermove', onTouchMove);
    on('pointerup', onTouchUp); on('pointercancel', onTouchUp);
    const onResize = () => { this.overlayCam.setSize(this.scale.width, this.scale.height); this.clampGoal(); this.applyCamera(); this.redraw(); };
    this.scale.on(Phaser.Scale.Events.RESIZE, onResize); this.unsubs.push(() => this.scale.off(Phaser.Scale.Events.RESIZE, onResize));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.emotes.destroy();
      this.matterFx.destroy();
      this.spells.clear();
      this.combat.clear();
      for (const u of this.unsubs) u();
      this.unsubs = [];
      usePlayback.getState().setHasPlayer(false);
      useView.getState().setSky(undefined);
      useView.getState().setField(undefined);
      setAmbience(undefined);
      this.fieldKey = '';
      useCinematic.getState().setFocus(undefined);
      this.registry.set('encounterReady', false);
    });

    this.redraw();
    this.registry.set('encounterReady', !!this.snapshot);

    // Test hooks (e2e/helpers.ts): board geometry and unit tiles, read-only.
    const w = window as unknown as Record<string, unknown>;
    w['__fkBoard'] = () => ({ zoom: this.cam.zoom, cell: this.cell, scrollX: this.boardCam.scrollX, scrollY: this.boardCam.scrollY, settled: this.settled(), cam: { ...this.cam }, goal: { ...this.goal }, drawn: this.drawnZoom, anchor: this.anchor && { ...this.anchor }, pinch: this.pinch && { ...this.pinch, touches: [...this.touches.values()] } });
    w['__fkUnitTile'] = (name: string) => this.snapshot?.units.find((u) => u.name === name)?.tile;
    // docs/60 review hook: centre the camera on a square (at a zoom), as a gallery capture needs
    w['__fkLookAt'] = (x: number, y: number, zoom?: number) => {
      if (!this.snapshot) return false;
      const z = zoom ? clampZoom(zoom) : this.goal.zoom, r = this.rect, p = this.mapper.tileCentre({ x, y });
      this.anchor = undefined; this.easing = 'pan';
      this.goal = { zoom: z, x: p.x - (r.x + r.w / 2) / z, y: p.y - (r.y + r.h / 2) / z };
      this.clampGoal();
      this.cam = { ...this.goal }; this.drawnZoom = z;
      if (useView.getState().zoomGoal !== z) useView.setState({ zoomGoal: z, zoomAnchor: undefined });
      this.applyCamera(); this.redraw();
      return true;
    };
    // docs/41: the room's mechanisms for the e2e specs
    w['__fkFixtures'] = () => (this.snapshot?.fixtures ?? []).map((f) => ({ id: f.id, kind: f.kind, name: f.name, on: f.on, x: f.tile.x, y: f.tile.y, ...(f.lock ? { lock: f.lock } : {}) }));
    // docs/113: who stands on the board and how hurt (wounds carried along a quest's path)
    w['__fkUnits'] = () => (this.snapshot?.units ?? []).map((u) => ({ name: u.name, heroId: u.heroId, hp: u.hp, hpMax: u.hpMax }));
    w['__fkTiles'] = () => { const out: Record<string, number> = {}; for (const t of this.snapshot?.board.tiles ?? []) out[t.terrain] = (out[t.terrain] ?? 0) + 1; return out; };
    w['__fkAuthoring'] = useAuthoring;
    w['__fkLighting'] = () => this.lighting.info();
    w['__fkStrike'] = () => this.lighting.strike();
    w['__fkLeap'] = () => this.lighting.leap();
    w['__fkFog'] = () => this.fog.info();
    // docs/186: the commissioned terrain on show (set, standing images, paint and upload ms)
    w['__fkTerrain'] = () => this.tiles.art?.info();
    w['__fkSeeThrough'] = () => this.tiles.art?.seeThroughInfo();
    w['__fkWild'] = () => this.wild.info();
    // docs/39 FW6 review hooks: slow the scene's clock (e.g. 0.1) so a capture can catch a walk
    // mid-corner, and read where a figure stands on screen (board px, feet) mid-tween.
    w['__fkPlaybackSlow'] = (slow = 1) => { this.tweens.timeScale = slow; this.time.timeScale = slow; };
    w['__fkReach'] = () => {
      const r = this.reachCache?.reach;
      if (!r) return undefined;
      const f = r.field;
      return {
        tone: r.tone, stand: r.tiles, ...(r.quiet ? { quiet: true } : {}),
        ...(f ? { from: f.from, pace: f.pace, reach: f.reach.map((x) => ({ tile: x.tile, cost: x.cost, stand: x.stand })) } : {}),
        ...(r.aim ? { from: r.aim.from, aim: r.aim.tiles, targets: r.targets } : {}),
      };
    };
    w['__fkUnitPx'] = (name: string) => { const u = [...this.units.values()].find((x) => x.unit.name === name); return u ? { x: u.x, y: u.y, cell: this.cell, tile: { ...u.tile }, facing: faceOf(u.sprite), w: u.sprite.displayWidth, h: u.sprite.displayHeight, lift: u.lift, depth: u.depth, angle: u.angle, water: u.water.pose, cut: u.water.cutNow } : undefined; };
    // Chips and floating numbers use the kit's web fonts: redraw once they have loaded (unless the
    // scene has shut down meanwhile: its layers are gone).
    let fontsFor = true;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { fontsFor = false; });
    void document.fonts?.load('700 11px "Alegreya Sans"').then(() => document.fonts.load('900 20px Cinzel')).then(() => { if (!fontsFor) return; this.intent.reset(); this.redraw(); }).catch(() => undefined);
    // docs/22 O4 review hook: play a getaway on a named unit without winning the fight.
    // `slow` (e.g. 0.25) runs the scene's clock slower so a capture can catch every frame.
    w['__fkEscapeFx'] = (name: string, kind: Escape, slow = 1) => {
      const u = [...this.units.values()].find((x) => x.unit.name === name);
      if (!u) return false;
      this.tweens.timeScale = slow; this.time.timeScale = slow;
      void playEscape({ scene: this, add: (o) => { this.layers.effects.add(o); }, cell: this.cell, boardW: this.snapshot?.board.w ?? 0, speed: 1, reduced: false }, u, kind)
        .then(() => { this.tweens.timeScale = 1; this.time.timeScale = 1; this.time.delayedCall(500, () => u.reset(u.unit.tile, this.cell)); });
      return true;
    };
    w['__fkEmoteBubbles'] = () => this.emotes.drawnIds();
    w['__fkFallen'] = () => ({ drawn: this.corpses.count(), bodies: this.snapshot?.board.fallen ?? [] });
    // docs/66: the pending call's marks on the board, and a way to play a call's moment without a server
    w['__fkCalls'] = () => ({ drawn: this.calls.drawn, pending: this.snapshot?.pending ?? [] });
    w['__fkCallMoment'] = (e: Parameters<NonNullable<EffectPlayerHost['call']>>[0], ms = 2400) => this.callMoment(e, ms);
    // the cards over the board (screen CSS px), one put up by hand, and the board set again as a new
    // one would be (it kills every tween) — a card must still go on time, or at a click
    w['__fkBanners'] = () => this.liveBanners.map((b) => ({ x: b.c.x, y: b.c.y, w: b.w, h: b.h, rest: b.x }));
    w['__fkBanner'] = (text: string, ms = 3000, style: 'event' | 'depth' | 'chamber' | 'danger' = 'event', over?: string) => this.banner(text, ms, style, over);
    w['__fkRestage'] = () => { if (this.snapshot) this.setSnapshot(this.snapshot); };
    w['__fkUnitNames'] = () => (this.snapshot?.units ?? []).map((u) => ({ name: u.name, faction: u.faction, onBoard: u.onBoard !== false, unitId: u.unitId, size: u.size ?? 1, tile: { ...u.tile } }));
    w['__fkThumps'] = () => [...this.thumps];
    // docs/115: the things and matter on the board as drawn, and every reaction row played
    w['__fkGround'] = () => ({ things: (this.snapshot?.things ?? []).map((t) => ({ ...t })), matter: [...(this.snapshot?.board.matter ?? [])], reacted: [...this.reacted] });
    // docs/92: the huge foes' reveals played, the bodies drawn from commissioned art, and a moment played by hand
    w['__fkHugeReveals'] = () => [...this.hugeReveals];
    w['__fkHugeBodies'] = () => [...this.units.values()].filter((u) => u.huge).map((u) => ({ unitId: u.unit.unitId, kind: u.huge!.kind, size: u.size, key: u.sprite.texture.key, width: u.sprite.displayWidth }));
    w['__fkHugeMoment'] = (unitId: number, move: string) => { const u = this.units.get(unitId); if (u?.huge) this.hugeMove(u, move, 900); };
    // docs/117 TT6: the arms up, risen and sunk this scene; and one arm's emergence played again by hand
    w['__fkTentacles'] = () => this.tentacles.info();
    w['__fkTentacleRise'] = (unitId: number, delayMs = 0) => { const u = this.units.get(unitId); if (u) this.tentacles.emerge(u, delayMs, true); return !!u; };
    w['__fkEnemies'] = () => (this.snapshot?.units ?? []).filter((u) => u.faction === 'enemy' && u.onBoard !== false && !u.downed).map((u) => ({ ...u.tile, name: u.name, unitId: u.unitId }));
  }

  override update(time: number, delta: number): void {
    this.stepCamera(delta);
    this.dashPhase = (this.dashPhase + delta * 0.01) % 1000;
    if (Math.floor(this.dashPhase * 4) !== Math.floor((this.dashPhase - delta * 0.01) * 4)) this.intent.tick(this.dashPhase);
    this.lighting.update(time);
    this.fog.update(time);
    this.tiles.update(time);
    // docs/35: the units layer sorts by where things stand on the ground (feet), creatures included
    // (docs/102 FL3: a figure in the air over all of them)
    for (const u of this.units.values()) { const d = u.standDepth(); if (u.depth !== d) u.setDepth(d); }
    this.seeThrough(time);
    // a carried key or relic walks with its bearer
    this.fixtures.follow((id) => this.units.get(id));
    this.wild.update(wildFeed.rows(), serverNow(), delta);
    this.explore.update(time);
    this.emotes.update(this.units, useSettings.getState().reduceMotion || motionMs(1) === 0);
  }

  /**
   * docs/186 EA12: the standing pieces fade over the figures drawn here (a foe the fog hides is not drawn, so it fades
   * nothing), the bodies on the ground and the square under the local mouse.
   */
  private seeThrough(time: number): void {
    const art = this.tiles.art;
    if (!art?.painted) return;
    const targets: SeeTarget[] = [];
    for (const u of this.units.values()) {
      if (!u.active || !u.visible || u.alpha < 0.05) continue;
      const b = u.sprite.getBounds(this.seeRect);
      targets.push(unitTarget({ x: b.x, y: b.y, w: b.width, h: b.height }, u.depth));
    }
    for (const b of this.corpses.bodies()) targets.push(unitTarget(b, b.depth));
    if (this.seeHover && !this.press) targets.push(squareTarget(this.seeHover, this.cell));
    art.seeThrough(targets, time);
  }
  private readonly seeRect = new Phaser.Geom.Rectangle();

  // ---- board ------------------------------------------------------------------------------------

  /** docs/41 PZ8: the room's mechanisms, with only the workings the bought hints showed. */
  private drawFixtures(snapshot: Snapshot): void {
    const { hinted, stepped, focus } = usePuzzle.getState();
    const w = snapshot.board.w;
    const ice = snapshot.board.tiles.flatMap((t, i) => (t.ice ? [{ x: i % w, y: Math.floor(i / w) }] : []));
    this.fixtures.draw(snapshot.fixtures, hintReveal(snapshot.fixtures ?? [], hinted, stepped), ice);
    this.fixtures.focus(focus);
  }

  private setSnapshot(snapshot: Snapshot): void {
    const first = !this.snapshot || this.snapshot.board.w !== snapshot.board.w || this.snapshot.board.h !== snapshot.board.h;
    const prev = this.snapshot;
    this.snapshot = snapshot;
    if (this.facingFor !== snapshot.encounterId) { this.facing.clear(); this.facingFor = snapshot.encounterId; }
    // (the one-shot flares and rings those tweens would have removed go with them, board/oneShots)
    killTweens(this);
    // a trap's pieces went with their tweens too (as on a skip)
    this.snareFx.clear();
    // killAll stopped the fades of floating numbers and pings (they would hang on the board) and
    // the glyph layer's own loops: drop the numbers, let the pings go, rebuild the glyphs — and
    // give the cards still up their fades back (a wonder's card outlasts the pulse that showed it)
    for (const o of [...this.overlay.pings.list]) o.destroy();
    this.rearmBanners();
    this.intent.reset();
    this.tiles.draw(snapshot);
    this.water.setBoard(snapshot.board);
    this.emotes.setBoard(snapshot, useSession.getState().identity ?? '');
    this.stashes.draw(snapshot.stashes);
    this.objectives.draw(snapshot);
    this.drawFixtures(snapshot);
    this.matterFx.set(snapshot.board.matter, snapshot.things);
    this.hidden.draw(snapshot.fixtures, snapshot.board);
    this.crawlProps.draw(snapshot.board, snapshot.crawl, snapshot.fixtures);
    this.snareProps.draw(snapshot);
    this.explore.draw(snapshot.board, snapshot.explore, snapshot.fixtures, { crawl: snapshot.crawl, units: snapshot.units });
    this.calls.draw(snapshot);
    // docs/31: under fog, units that came into the party's sight rise out of it and units that left fade into it
    // (from the units drawn: the playback may have brought some into sight or let some go already)
    const sight = !first && prev && (prev.fog || snapshot.fog) ? unitChanges(this.units.keys(), onBoard(snapshot)) : undefined;
    this.buildUnits(sight?.left);
    // docs/117 TT6: arms that were not up on the last board rise now; arms gone under sink
    this.tentacles.begin(snapshot.encounterId);
    this.tentacles.clear();
    this.tentacles.board(snapshot.units, this.units);
    this.drawCorpses(snapshot);
    this.relight(snapshot);
    // docs/93 §5.6: the ambient fish keep out of a pool a water foe lurks in
    this.lighting.quietPools(snapshot.units.filter((u) => u.faction === 'enemy' && u.onBoard !== false && !u.downed && swims(gaitKey(u.gait))).flatMap((u) => tilesAt(u.tile, sizeOf(u))), snapshot.board.w);
    this.refog(snapshot);
    for (const id of sight?.entered ?? []) { const u = this.units.get(id); if (u) this.fog.enter(u); }
    if (first) { this.fitted = false; this.fitAndCentre(); }
    this.redraw();
    this.registry.set('encounterReady', true);
    this.registry.set('boardHash', snapshot.snapshotHash);
  }

  /** docs/50: the fallen lie where they dropped — but the first named foe got away (docs/22 O4). */
  private drawCorpses(snapshot: Snapshot): void {
    const n = useCinematic.getState().namedFoe;
    const encounterId = useEncounter.getState().encounterId;
    this.corpses.draw(snapshot, (u) => !!n && isNamedFoe(n, encounterId, u));
  }

  private buildUnits(leaving: number[] = []): void {
    for (const [id, u] of this.units) { if (leaving.includes(id) && this.fog.active) this.fog.leave(u); else u.destroy(); }
    this.units.clear();
    if (!this.snapshot) return;
    // docs/56 EP5: once the party has fallen, its heroes lie where they fell under the defeat card
    const d = useCinematic.getState().defeat;
    const lost = !!d && d.encounterId === useEncounter.getState().encounterId;
    for (const u of this.snapshot.units) {
      if (u.onBoard === false) continue;
      const s = new UnitSprite(this, u, this.manifest);
      const face = this.facing.get(u.unitId);
      if (face) s.setFacing(face);
      this.layers.units.add(s);
      this.units.set(u.unitId, s);
      this.maybeReveal(s);
      if (u.downed && lost && u.heroId !== undefined) s.fall({ unit: u.unitId, tile: { ...u.tile }, size: s.size, turn: 90, dx: 0, dy: 0 }, this.cell, 0);
      else if (u.downed) s.setDimmed(true);
    }
  }

  /**
   * docs/19: light the room at its hour, docs/20: in its weather. The light map is rebuilt only when
   * what it depends on changes (kit, hour, weather, walls, doors, fires, exits) so flames, rain and
   * particles run on across pulses.
   */
  private relight(snapshot: Snapshot): void {
    const board = snapshot.board;
    const kit = this.tiles.kitForBoard(board).key;
    const hour = this.hourOf(board);
    const encounterId = useEncounter.getState().encounterId;
    const weather = weatherOverride() ?? encounterWeather({ weather: board.weather, encounterId, seed: board.seed }, kit, climateFor(kit).setting);
    const season = seasonOverride() ?? parseSeason(board.season);
    // docs/61 EX13: a wonder not yet beheld glimmers in the dark like a shrine's glow (the sim's `WONDER_GLOW`)
    const glimmers = (snapshot.explore?.wonders ?? []).flatMap((w) => {
      const f = w.found ? undefined : snapshot.fixtures?.find((x) => x.id === w.fixture);
      return f ? [{ tile: f.tile, radius: 2, look: 4 }] : [];
    });
    // docs/114: lit things and what burns on the ground light the board too
    const lights = [...(snapshot.crawl?.lights ?? []), ...glimmers, ...matterLights(snapshot)];
    // docs/63: no torch burns where the party has never seen past a closed door (docs/118 PT12: nor in a crawl)
    const fog = snapshot.fog;
    const unseen = fog && fog.w === board.w && blindFog(fog) ? fog.cells.map((c) => c === 0) : undefined;
    const key = `${unseen ? unseen.map((u) => (u ? 1 : 0)).join('') : ''}|${kit}|${hour}|${weather.kind}:${weather.intensity}:${weather.wind}|${season ?? ''}|${board.w}x${board.h}|${this.tiles.artId}|${lights.map((l) => `${l.tile.x},${l.tile.y},${l.look},${l.radius}`).join(';')}|${board.tiles.map((t) => `${t.terrain[0]}${t.doorOpen ? 'o' : ''}${t.hazard ?? ''}${t.exit ? 'x' : ''}`).join('')}`;
    if (key !== this.lightKey) {
      this.lightKey = key;
      // docs/34: the place's sound follows the same land, hour and sky (and the storm's schedule its flashes)
      setAmbience(ambienceFor(snapshot, kit, hour, weather));
      // docs/60 DC7: a crawl's sconces, braziers, candles, moss and shrines light their chambers
      this.lighting.setExtraLights(lights);
      this.lighting.setUnseen(unseen);
      // docs/186 EA5: drawn in the commissioned art, the lights and liquids are the painted board's
      this.lighting.setArt(this.tiles.painted);
      this.lighting.setBoard(board, this.tiles.tiled, kit, hour, weather, season);
      this.wild.setBoard(board, this.tiles.tiled, kit, this.tiles.painted ? this.tiles.art?.occluders() : undefined);
      useView.getState().setSky({ hour, phase: this.lighting.dayLight?.phase ?? '', weather: weatherLabel(weather) });
    }
    this.lightUnits();
  }

  private lightUnits(): void {
    // each hero's base ring in its player's colour, as its sight rim is (refog)
    const { owners, colours: chosen } = useEncounter.getState();
    const seats = playerSeats(this.snapshot?.units ?? [], owners, chosen);
    for (const s of this.units.values()) {
      const owner = s.unit.heroId !== undefined ? owners[s.unit.heroId] : undefined;
      if (owner !== undefined && s.unit.faction === 'hero') s.setRingColour(seatColour(seats.get(owner)));
    }
    this.lighting.setUnits([...this.units.values()].map((s) => ({ key: String(s.unit.unitId), obj: s, body: s.sprite, hero: s.unit.faction === 'hero', down: !!s.unit.downed, stand: () => s.stand })));
  }

  /**
   * docs/56: a group of clips has played — what it did now stands: the ground it changed (a door,
   * a gate, a lever, a lit brazier, set terrain) and the light through it, the party's sight from
   * where it now stands (docs/31: foes coming into it rise out of the fog, foes leaving it fade),
   * and the HUD's HP and foes.
   */
  private landed(entries: ResolutionEntry[]): void {
    this.hugeMoments(entries);
    // docs/140 DT13: the storyteller's ending as the dweller falls, the treasure's words as it is taken
    const after = useEncounter.getState().snapshot;
    for (const en of entries) {
      const c = storyCallout(en.effect, after?.crawl ?? this.snapshot?.crawl, (id) => (after?.fixtures ?? this.snapshot?.fixtures ?? []).find((f) => f.id === id) ?? (this.snapshot?.fixtures ?? []).find((f) => f.id === id));
      if (c) this.callout(c);
    }
    const live = this.live;
    if (!live) return;
    const ch = live.apply(entries);
    if (ch.ground) {
      const s = live.snapshot;
      this.tiles.draw(s);
      this.water.setBoard(s.board);
      this.stashes.draw(s.stashes);
      this.drawFixtures(s);
      this.matterFx.set(s.board.matter, s.things);
      this.hidden.draw(s.fixtures, s.board);
      this.crawlProps.draw(s.board, s.crawl, s.fixtures);
      this.snareProps.draw(s);
      this.explore.draw(s.board, s.explore, s.fixtures, { crawl: s.crawl, units: s.units });
      this.relight(s);
    }
    // docs/130 OB3: a task worked, a relic taken, an escorted ally walking
    if (ch.ground || ch.units) this.objectives.draw(live.snapshot);
    if (ch.sight) this.refogLive(live);
    if (this.sightUnits(live) || ch.units || ch.sight || ch.ground) usePlayback.getState().setLive(live.snapshot);
  }

  /** docs/31: the party's fog from the board as it stands, asked of the sim (the walls and doors it reads included). */
  private refogLive(live: LiveBoard): void {
    const bridge = getBridge(), handles = playbackHandles();
    if (!bridge || !handles || !live.snapshot.fog) return;
    let fog;
    try { fog = bridge.fogBetween(handles.start, handles.end, [...live.changed], live.partyStands()); } catch { return; }
    if (!fog) return;
    live.setFog(fog);
    this.refog(live.snapshot);
  }

  /**
   * docs/31: foes of the board to come rise out of the fog where they stand once the party sees
   * them (or the square their next step takes), before their own entries play; foes out of its
   * sight with nothing left to do in view fade into it. True when the units shown changed.
   */
  private sightUnits(live: LiveBoard): boolean {
    const fog = live.snapshot.fog;
    if (!fog) return false;
    const seen = (t: Tile | undefined) => !!t && fog.cells[t.y * fog.w + t.x] === 2;
    // docs/79: a huge body is in sight when any square it covers is
    const bodySeen = (t: Tile | undefined, size: number | undefined) => !!t && tilesAt(t, sizeOf({ size })).some(seen);
    let changed = false;
    for (const u of useEncounter.getState().snapshot?.units ?? []) {
      if (this.units.has(u.unitId)) continue;
      const a = live.arrival(u.unitId);
      if (!a || !(bodySeen(a.tile, a.size) || bodySeen(live.nextStep(u.unitId), a.size))) continue;
      live.add(a);
      const sprite = new UnitSprite(this, a, this.manifest);
      const face = this.facing.get(a.unitId);
      if (face) sprite.setFacing(face);
      this.layers.units.add(sprite);
      this.units.set(a.unitId, sprite);
      this.fog.enter(sprite);
      this.maybeReveal(sprite);
      changed = true;
    }
    for (const [id, sprite] of this.units) {
      const u = live.snapshot.units.find((x) => x.unitId === id);
      if (!u || u.faction !== 'enemy' || sprite.fallen || bodySeen(u.tile, u.size) || live.acts(id)) continue;
      this.fog.leave(sprite);
      this.units.delete(id);
      live.remove(id);
      changed = true;
    }
    if (changed) this.lightUnits();
    return changed;
  }

  /** docs/31: the fog of war as the party sees it, each party unit's sight rim in its player's colour. */
  private refog(snapshot: Snapshot): void {
    const { owners, colours: chosen } = useEncounter.getState();
    const seats = playerSeats(snapshot.units, owners, chosen);
    const colours: FogEyeColour[] = (snapshot.fog?.eyes ?? []).map((e) => {
      const u = snapshot.units.find((x) => x.unitId === e.unit);
      const owner = u?.heroId !== undefined ? owners[u.heroId] : undefined;
      return { unit: e.unit, colour: owner !== undefined ? seatColour(seats.get(owner)) : 0xe8e2d0, ...(u ? { tile: u.tile } : {}) };
    });
    this.fog.setMode({ ...fogMode(useSettings.getState()), wind: this.lighting.weatherNow.wind, weather: this.lighting.weatherNow.kind });
    this.fog.set(snapshot.board, snapshot.fog, colours);
    this.tiles.art?.setFog(snapshot.fog, this.fog.veil);
    // docs/63: behind a puzzle room's closed doors the edge covers what the party has never seen
    const field = closedDoorsField(snapshot.board, snapshot.fog);
    this.edge.draw(field);
    // docs/118 PT12: and the explore air keeps to what was seen
    this.explore.setSeen(blindFog(snapshot.fog) ? field : undefined);
  }

  private tileAtScreen(sx: number, sy: number): Tile | undefined {
    if (!this.snapshot) return undefined;
    const wx = sx / this.cam.zoom + this.cam.x, wy = sy / this.cam.zoom + this.cam.y;
    const t = { x: Math.floor(wx / this.cell), y: Math.floor(wy / this.cell) };
    // docs/38 BS1: a void square is no board — nothing to hover, click or ping there
    // docs/63: behind a closed door the party has never seen past the cursor still goes (a teleport
    // may aim there), though the board says nothing of what is there, not even whether it is board
    const { board, fog } = this.snapshot;
    if (t.x >= 0 && t.y >= 0 && t.x < board.w && t.y < board.h && unknownSquare(fog, t)) return t;
    return squareOnBoard(closedDoorsField(board, fog), t) ? t : undefined;
  }

  // ---- camera (docs/33 E4–E6) ---------------------------------------------------------------

  private get geom() { return { w: this.snapshot?.board.w ?? 1, h: this.snapshot?.board.h ?? 1, cell: this.cell, top: this.tiles?.headroom ?? 0 }; }

  /** The fight's hour (docs/19): `?hour=`, the room's own, or the encounter's seeded one. */
  private hourOf(board: Snapshot['board']): number {
    return hourOverride() ?? encounterHour({ hour: board.hour, encounterId: useEncounter.getState().encounterId, seed: board.seed });
  }

  /**
   * docs/186 EA5: the fight's set has loaded (or the World's biome came in): the board as it stands now, redrawn in
   * it, lit by its lights and fogged; the camera keeps where it is (now with the art's headroom above the board).
   */
  private artLanded(): void {
    const s = this.live?.snapshot ?? this.snapshot;
    if (!s || !this.scene.isActive()) return;
    this.tiles.draw(s);
    this.relight(s);
    this.tiles.art?.setFog(s.fog, this.fog.veil);
    this.clampGoal();
  }
  private get rect() { return useView.getState().viewport ?? { x: 0, y: 0, w: this.scale.width / this.dpr, h: this.scale.height / this.dpr }; }

  /** The player's zoom preference (or the fit), the board centred; the first time it snaps there. */
  private fitAndCentre(snap = !this.fitted, whole = false): void {
    if (!this.snapshot) return;
    // docs/60 DC14, docs/66 RM13: every fight opens on the party wherever it stands, never on the
    // whole board (V fits it on demand)
    // (a puzzle room is one room: it is still shown whole)
    if (!whole && this.snapshot.objective !== 'Solve' && this.openOnParty(snap)) return;
    const pref = useSettings.getState().zoomPref;
    const r = this.rect;
    const zoom = pref === 'fit' ? fitStop(this.geom, r.w, r.h, 32, this.dpr) : stepZoom(pref, 0, this.dpr);
    this.anchor = undefined; this.easing = 'zoom';
    this.goal = { zoom, ...centredScroll(this.geom, zoom, r) };
    if (snap) { this.cam = { ...this.goal }; this.drawnZoom = zoom; }
    this.fitted = true;
    if (useView.getState().zoomGoal !== zoom) useView.setState({ zoomGoal: zoom, zoomAnchor: undefined });
    this.applyCamera();
    if (snap) this.redraw();
  }

  /**
   * docs/60 DC14: frame the party — its heroes' middle, at a zoom showing a comfortable stretch of
   * the crawl round them (the player's zoom preference when it has one). False with no hero on the board.
   */
  private openOnParty(snap: boolean): boolean {
    const heroes = this.snapshot!.units.filter((u) => u.heroId !== undefined && u.onBoard !== false).map((u) => u.tile);
    const f = partyFrame(heroes);
    if (!f) return false;
    const pref = useSettings.getState().zoomPref;
    const r = this.rect;
    const zoom = pref === 'fit' ? stepZoom(clampZoom(Math.min(r.w, r.h) / (f.span * this.cell)), 0, this.dpr) : stepZoom(pref, 0, this.dpr);
    this.anchor = undefined; this.easing = 'zoom';
    this.goal = { zoom, x: f.x * this.cell - (r.x + r.w / 2) / zoom, y: f.y * this.cell - (r.y + r.h / 2) / zoom };
    this.clampGoal();
    if (snap) { this.cam = { ...this.goal }; this.drawnZoom = zoom; }
    this.fitted = true;
    if (useView.getState().zoomGoal !== zoom) useView.setState({ zoomGoal: zoom, zoomAnchor: undefined });
    this.applyCamera();
    if (snap) this.redraw();
    return true;
  }

  /**
   * docs/60 DC14: a hero went down a stair or a chute (or through a portal stone) to a far part of
   * the board. The camera goes with it when it was watching where the hero stood, or the hero is the
   * one selected — another player's hero vanishing off-screen leaves the view alone.
   */
  private jumped(unitId: number, from: Tile, to: Tile, ms: number): void {
    const u = this.snapshot?.units.find((x) => x.unitId === unitId);
    if (!u || u.heroId === undefined || this.focused) return;
    // docs/118 PT10: only the camera of the player who owns the unit goes with it
    const { owners, me } = useEncounter.getState();
    if (!followsJump(u, owners, me, this.onScreen(from), useAuthoring.getState().selectedHero === u.heroId)) return;
    const r = this.rect, z = this.goal.zoom;
    const far = Math.hypot(to.x - from.x, to.y - from.y) * this.cell * z > Math.max(r.w, r.h) * 0.5;
    // a hop within sight of where the view already is (a short teleport) leaves it alone
    if (!far && this.onScreen(to)) return;
    // a stair or a chute is a cut, not a glide across the dark between the depths
    this.lookAt(to, far ? 'cut' : 'ease');
    void ms;
  }

  /** Whether square `t`'s centre is inside the board viewport where the camera is going. */
  private onScreen(t: Tile): boolean {
    const r = this.rect, z = this.goal.zoom, p = this.mapper.tileCentre(t);
    const sx = (p.x - this.goal.x) * z, sy = (p.y - this.goal.y) * z;
    return sx >= r.x && sx <= r.x + r.w && sy >= r.y && sy <= r.y + r.h;
  }

  /** Where a unit stands now: its figure (mid-playback), else the board. */
  private unitTile(unitId: number): Tile | undefined {
    return this.units.get(unitId)?.tile ?? this.snapshot?.units.find((u) => u.unitId === unitId)?.tile;
  }

  /** docs/118 PT8: a double-click on a roster card eases the camera over to that unit. */
  private lookAtUnit(unitId: number): void {
    const t = this.unitTile(unitId);
    if (t) this.lookAt(t, 'ease');
  }

  /** Centre square `t` at the current zoom: the camera eases there (`cut`: jumps). */
  private lookAt(t: Tile, how: 'ease' | 'cut'): void {
    if (!this.snapshot || this.focused) return;
    const r = this.rect, z = this.goal.zoom, q = this.mapper.tileCentre(t);
    this.anchor = undefined; this.easing = 'pan';
    this.goal.x = q.x - (r.x + r.w / 2) / z; this.goal.y = q.y - (r.y + r.h / 2) / z;
    this.clampGoal();
    if (how === 'cut') { this.cam = { ...this.goal }; this.applyCamera(); }
  }

  /** docs/60: show a crawl moment — a depth or chamber banner, a call-out, the journal, a shrine's glow. */
  private crawlMoment(e: Parameters<NonNullable<EffectPlayerHost['crawl']>>[0], ms: number): void {
    const next = useEncounter.getState().snapshot;
    const crawl = next?.crawl ?? this.snapshot?.crawl;
    const names = new Map([...(this.snapshot?.units ?? []), ...(next?.units ?? [])].map((u) => [u.unitId, u.name] as const));
    const c = crawlCallout(e, crawl, (id) => names.get(id));
    // docs/61: an exploration's levels are its reaches
    if (c && (next?.explore ?? this.snapshot?.explore) && c.eyebrow) c.eyebrow = c.eyebrow.replace(/^Depth/, 'Reach');
    if (e.kind === 'LoreRead') useCrawl.getState().flashLore(e.lore);
    if (e.kind === 'ShrineAnswered') {
      const colour = BOON_WORDS[e.boon]?.colour ?? 0xffd166;
      for (const id of e.touched) { const u = this.units.get(id); if (u) this.hidden.glint(u.tile, Math.max(ms, 700), colour); }
    }
    if (e.kind === 'Fell') this.hidden.snap(e.from, ms);
    if (c) this.callout(c);
  }

  /** Whether the party sees square `t` (on the board before the pulse or after it): the blind crawl fog draws nothing else. */
  private partySees(t: Tile): boolean {
    const ok = (s: Snapshot | undefined) => { const f = s?.fog; return !f || (f.cells[t.y * f.w + t.x] ?? 0) !== 0; };
    return ok(this.snapshot) || ok(useEncounter.getState().snapshot);
  }

  /**
   * docs/140 DT4, DT14: a dungeon trap's moment. Its spring sends the camera to the trap (to the foe
   * for a card: the foe's cry in a bubble and a cackle first) and raises the big card — TRAP!, its
   * name, its words, its guise unmasked; each beat is drawn by its act; its end, a find and a disarm
   * are small tags.
   */
  private snareMoment(e: Parameters<NonNullable<EffectPlayerHost['snare']>>[0], ms: number): void {
    const next = useEncounter.getState().snapshot;
    const s = snareOf(next?.crawl, e.snare) ?? snareOf(this.snapshot?.crawl, e.snare);
    this.snareMoments.push({ kind: e.kind, snare: e.snare, ...(e.kind === 'SnareActed' ? { act: e.act } : {}), at: this.time.now });
    const reduced = useSettings.getState().reduceMotion || motionMs(1) === 0;
    switch (e.kind) {
      case 'SnareSprung': {
        const units = [...(this.snapshot?.units ?? []), ...(next?.units ?? [])];
        const c = snareCallout(e, next?.crawl ?? this.snapshot?.crawl, (id) => units.find((u) => u.unitId === id)?.name);
        const card = e.by !== undefined;
        const foeAt = card ? this.units.get(e.by!)?.tile : undefined;
        const look = foeAt ?? e.tile;
        const seen = this.partySees(look) || (e.victim !== undefined && this.units.get(e.victim)?.visible !== false);
        if (seen && !reduced) {
          const cine = useCinematic.getState();
          if (!cine.faceoffPlaying) {
            cine.setFocus(look, card ? 1.9 : 1.6);
            this.snareFocus = { snare: e.snare, tile: look };
            this.time.delayedCall(Math.max(ms * 2.2, 4200), () => this.releaseSnareFocus(e.snare));
          }
        }
        const raise = () => {
          this.snareFx.sprung(e.tile, ms, card);
          const hold = Math.max(ms * 2.2, c.line.length > 90 ? 6200 : 5000);
          this.banner(c.line ? `${c.head} — ${c.line}` : c.head, motionMs(hold) || hold, 'trap', c.eyebrow, c.reveal ? `${c.reveal} … was a trap all along` : undefined);
        };
        if (card) {
          // the foe plays its card: it rears up, says its line, cackles; a beat, then the trap
          const foe = this.units.get(e.by!);
          if (foe) this.tweens.add({ targets: foe, scaleX: 1.2, scaleY: 1.2, duration: Math.max(1, ms * 0.12), yoyo: true, repeat: 1, ease: 'Sine.easeInOut' });
          if (c.cry) this.speak(e.by!, c.cry, Math.max(ms * 0.9, 2600));
          if (snarePresentation.localSounds) this.time.delayedCall(Math.min(500, ms * 0.2), () => playSfx('move.cackle', { pan: this.panOf(e.by) }));
          this.time.delayedCall(ms * 0.45, raise);
        } else raise();
        return;
      }
      case 'SnareActed': {
        this.snareFx.act(e, s?.def ?? -1, s?.anchor, ms);
        return;
      }
      case 'SnareEnded': {
        const at = s?.anchor;
        if (at) { this.snareFx.tag(at, 'THE TRAP IS SPENT', '#c9bfa8'); this.snareFx.ended(at); }
        this.releaseSnareFocus(e.snare);
        return;
      }
      case 'SnareFound': {
        if (s) this.snareFx.found(underfoot(s), Math.max(ms, 700));
        return;
      }
      case 'SnareDisarmed': {
        this.snareFx.found([e.tile], Math.max(ms, 600));
        return;
      }
    }
  }

  private releaseSnareFocus(snare: number): void {
    const f = this.snareFocus;
    if (!f || f.snare !== snare) return;
    this.snareFocus = undefined;
    const cine = useCinematic.getState();
    if (cine.focus?.x === f.tile.x && cine.focus?.y === f.tile.y) cine.setFocus(undefined);
  }

  /** docs/61: show an exploration moment — a wonder's card and flare, a veil blowing apart, a vantage's ring, a way named, the party going on. */
  /**
   * docs/66 RM10: a call or a raising as it lands — the leader's line in a speech bubble over it,
   * a banner saying who comes and from where; a broken leader's banner says nobody answers.
   */
  private callMoment(e: Parameters<NonNullable<EffectPlayerHost['call']>>[0], ms: number): void {
    const next = useEncounter.getState().snapshot;
    const units = [...(this.snapshot?.units ?? []), ...(next?.units ?? [])];
    const name = (id: number) => units.find((u) => u.unitId === id)?.name ?? 'The leader';
    const b = callBanner(e, name, this.snapshot ?? next);
    if (!b) return;
    const hold = Math.max(ms, b.danger ? 3200 : 4200);
    this.banner(b.text, motionMs(hold) || hold, b.danger ? 'danger' : 'event', b.eyebrow);
    if (b.speaker !== undefined && b.line) this.speak(b.speaker, b.line, hold);
  }

  /** A speech bubble over a unit for `ms` (world space: it rides with the board). */
  private speak(unitId: number, line: string, ms: number): void {
    const u = this.units.get(unitId);
    const tile = u?.tile ?? this.snapshot?.units.find((x) => x.unitId === unitId)?.tile;
    if (!tile) return;
    const p = this.mapper.tileCentre(tile);
    const c = this.cell;
    const t = this.add.text(0, 0, `“${line}”`, { fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: `${Math.max(7, Math.round(c * 0.42))}px`, color: '#1a1006', wordWrap: { width: c * 7 }, align: 'center' }).setOrigin(0.5, 1).setResolution(this.dpr * 3);
    const pad = c * 0.18, w = t.width + pad * 2, h = t.height + pad * 2;
    const g = this.add.graphics();
    g.fillStyle(0xf4ead2, 0.96); g.fillRoundedRect(-w / 2, -h - c * 0.25, w, h, c * 0.2);
    g.lineStyle(1, 0x1a1006, 0.9); g.strokeRoundedRect(-w / 2, -h - c * 0.25, w, h, c * 0.2);
    g.fillTriangle(-c * 0.15, -c * 0.26, c * 0.15, -c * 0.26, 0, 0);
    t.setPosition(0, -c * 0.25 - pad);
    const box = this.add.container(p.x, p.y - c * 0.95, [g, t]).setDepth(10_000);
    this.layers.effects.add(box);
    box.setAlpha(0);
    this.tweens.add({ targets: box, alpha: 1, duration: 160 });
    // its fade is a tween a new board could kill: the scene clock takes it away whatever happens
    this.time.delayedCall(Math.max(800, ms), () => {
      if (!box.active) return;
      this.tweens.add({ targets: box, alpha: 0, duration: 260, onComplete: () => box.destroy() });
      this.time.delayedCall(320, () => box.destroy());
    });
  }

  private exploreMoment(e: Parameters<NonNullable<EffectPlayerHost['explore']>>[0], ms: number): void {
    const next = useEncounter.getState().snapshot;
    const x = next?.explore ?? this.snapshot?.explore;
    const names = new Map([...(this.snapshot?.units ?? []), ...(next?.units ?? [])].map((u) => [u.unitId, u.name] as const));
    const c = exploreCallout(e, x, (id) => names.get(id));
    if (e.kind === 'Discovered') {
      const w = x?.wonders[e.wonder];
      if (w) { this.explore.flare(w.fixture, ms); useExplore.getState().flashWonder(e.wonder); }
    }
    if (e.kind === 'VeilParted') this.explore.part(e.veil);
    if (e.kind === 'Glimpsed') this.explore.vantage(e.at, ms);
    if (c) this.exploreCard(c);
  }

  /**
   * docs/61 EX1: a search sweeps out from the searcher — the squares it reached light in rings by
   * their distance (nearest first) and fade.
   */
  private searchSweep(unit: number, tiles: readonly Tile[], ms: number): void {
    // docs/81 SA1: from where the searcher stands as it searches (after its own walk), not where the pulse found it
    const from = this.units.get(unit)?.tile ?? this.snapshot?.units.find((u) => u.unitId === unit)?.tile;
    if (!from || !tiles.length || motionMs(1) === 0) return;
    const c = this.cell;
    const g = oneShot(this, this.add.graphics());
    this.layers.low.add(g);
    const st = { k: 0 };
    const paint = () => {
      g.clear();
      for (const t of tiles) {
        const d = Math.abs(t.x - from.x) + Math.abs(t.y - from.y);
        const a = Math.max(0, 1 - Math.abs(st.k * 4 - d)) * 0.4;
        if (a <= 0) continue;
        g.fillStyle(0xffd166, a * 0.5).fillRect(t.x * c + 1, t.y * c + 1, c - 2, c - 2);
        g.lineStyle(1, 0xffe7a3, a).strokeRect(t.x * c + 1.5, t.y * c + 1.5, c - 3, c - 3);
      }
    };
    paint();
    this.tweens.add({ targets: st, k: 1.1, duration: Math.max(500, ms * 1.6), ease: 'Sine.easeOut', onUpdate: paint, onComplete: () => g.destroy() });
  }

  /**
   * docs/81 SA5: a locked action another player's change ruled out was taken back — a puff of
   * sparks whooshes off the square the hero would have acted from, so the change is seen.
   */
  private whooshStruck(heroId: number, slot: Slot): void {
    const a = useAuthoring.getState(), bridge = getBridge();
    const hero = this.snapshot?.units.find((u) => u.heroId === heroId);
    if (!hero || !this.snapshot) return;
    const at = (a.snapshot && bridge?.stands(a.snapshot, hero.unitId, slot).find((x) => x.unit === hero.unitId)?.tile) ?? hero.tile;
    const p = this.mapper.tileCentre(at), c = this.cell;
    const ring = oneShot(this, this.add.graphics());
    this.layers.effects.add(ring);
    const st = { k: 0 };
    const paint = () => { ring.clear(); ring.lineStyle(Math.max(1, c * 0.08), 0xffd166, 1 - st.k).strokeCircle(p.x, p.y, c * (0.3 + st.k * 0.9)); };
    paint();
    this.tweens.add({ targets: st, k: 1, duration: motionMs(520) || 1, ease: 'Quad.easeOut', onUpdate: paint, onComplete: () => ring.destroy() });
    if (motionMs(1) === 0) return;
    if (!this.textures.exists('fk-struck-mote')) {
      const g = this.make.graphics({}, false);
      g.fillStyle(0xffffff, 1).fillCircle(4, 4, 4);
      g.generateTexture('fk-struck-mote', 8, 8);
      g.destroy();
    }
    // a whoosh: motes swept off to one side and up, as a card flicked off the table
    // saturated motes drawn NORMAL: additive near-whites vanish on bright ground (docs/47)
    const e = this.add.particles(0, 0, 'fk-struck-mote', { emitting: false, lifespan: 700, speed: { min: c * 2.5, max: c * 5 }, angle: { min: -160, max: -20 },
      scale: { start: Math.max(0.5, c / 20), end: 0.1 }, alpha: { start: 1, end: 0 }, tint: [0xffd166, 0xff9f5a, 0xffe7a3], gravityY: c * 3 });
    this.layers.effects.add(e);
    e.explode(24, p.x, p.y);
    this.time.delayedCall(900, () => e.destroy());
  }

  private exploreCard(c: ExploreCallout): void {
    const text = c.line ? `${c.head.replace(/ — /g, ', ')} — ${c.line}` : c.head;
    const hold = c.kind === 'wonder' ? 6200 : c.kind === 'depart' ? 6000 : c.kind === 'way' ? 3600 : 3000;
    this.banner(text, motionMs(hold) || hold, c.kind === 'wonder' || c.kind === 'depart' ? 'depth' : 'event', c.eyebrow);
  }

  /** How long each crawl banner stays up (ms at 1×; it outlasts its clip so the words can be read). */
  private callout(c: Callout): void {
    const text = c.line ? `${c.head.replace(/ — /g, ', ')} — ${c.line}` : c.head;
    const hold = c.kind === 'ending' ? 8000 : c.kind === 'treasure' ? 5600 : c.kind === 'depth' ? 5200 : c.kind === 'chamber' ? (c.line ? 3600 : 2200) : c.kind === 'lore' ? 2200 : c.line ? 4200 : 2600;
    this.banner(text, motionMs(hold) || hold, c.kind === 'depth' || c.kind === 'ending' ? 'depth' : c.kind === 'chamber' || c.kind === 'lore' ? 'chamber' : c.kind === 'lair' || c.kind === 'roamer' || c.kind === 'fall' ? 'danger' : 'event', c.eyebrow);
  }

  /** Centre the selected (else first) hero at the current zoom. */
  private centreOnHero(): void {
    if (!this.snapshot) return;
    const sel = useAuthoring.getState().selectedHero;
    const heroes = this.snapshot.units.filter((u) => u.heroId !== undefined && u.onBoard !== false);
    const u = heroes.find((h) => h.heroId === sel) ?? heroes[0];
    if (!u) return;
    this.lookAt(this.unitTile(u.unitId) ?? u.tile, 'ease');
  }

  /** docs/42: scale the zoom by `factor` about `at` (CSS px; default: the view's centre) and tell the HUD. */
  private zoomAt(factor: number, at: { x: number; y: number } | undefined, easing: keyof typeof EASE): void {
    if (!this.snapshot || this.focused) return;
    const z = clampZoom(this.goal.zoom * factor);
    if (z === this.goal.zoom) return;
    this.easing = easing;
    this.zoomTo(z, at);
    this.publishZoom();
  }

  /** The HUD shows the goal; the store subscription knows its own publish and leaves the camera be. */
  private publishZoom(): void { if (useView.getState().zoomGoal !== this.goal.zoom || useView.getState().zoomAnchor) useView.setState({ zoomGoal: this.goal.zoom, zoomAnchor: undefined }); }

  /** docs/42 CM4: a released flick coasts on for about a tenth of a second of its last speed (a slow drag stops where it is). */
  private fling(trail: Array<{ t: number; sx: number; sy: number }>, upAt: number): void {
    const a = trail[0], b = trail[trail.length - 1];
    if (!a || !b || b.t - a.t < 8 || upAt - b.t > 60 || useSettings.getState().reduceMotion || motionMs(1) === 0) return;
    const coast = 0.09, max = 260;
    let dx = ((b.sx - a.sx) / (b.t - a.t)) * 1000 * coast, dy = ((b.sy - a.sy) / (b.t - a.t)) * 1000 * coast;
    const n = Math.hypot(dx, dy);
    if (n < 4) return;
    if (n > max) { dx *= max / n; dy *= max / n; }
    this.easing = 'pan';
    this.goal.x -= dx / this.cam.zoom; this.goal.y -= dy / this.cam.zoom;
    this.clampGoal();
  }

  /** Ease to a zoom, keeping the world point under `at` (CSS px; default: the view's centre) where it is. */
  private zoomTo(zoom: number, at?: { x: number; y: number }): void {
    if (!this.snapshot || this.focused) return;
    const r = this.rect;
    const sx = at?.x ?? r.x + r.w / 2, sy = at?.y ?? r.y + r.h / 2;
    const base = this.anchor && !at ? this.anchor : { sx, sy, wx: sx / this.cam.zoom + this.cam.x, wy: sy / this.cam.zoom + this.cam.y };
    this.anchor = base;
    this.goal.zoom = zoom;
    this.goal.x = base.wx - base.sx / zoom; this.goal.y = base.wy - base.sy / zoom;
    this.clampGoal();
  }

  private clampGoal(): void {
    if (!this.snapshot || this.focused) return;
    const c = clampScroll(this.geom, this.goal.zoom, this.rect, this.goal);
    if (this.anchor && (c.x !== this.goal.x || c.y !== this.goal.y)) this.anchor = undefined;
    this.goal.x = c.x; this.goal.y = c.y;
  }

  private settled(): boolean { return this.cam.zoom === this.goal.zoom && this.cam.x === this.goal.x && this.cam.y === this.goal.y && this.drawnZoom === this.cam.zoom; }

  /**
   * Per frame: held zoom keys scale the goal, held pan keys and a mouse resting at the window's edge
   * move it; the shown camera eases to it (reduce motion: jumps).
   */
  private stepCamera(delta: number): void {
    if (!this.snapshot) return;
    const dt = Math.min(250, delta) / 1000;
    let zoomDir = 0;
    for (const id of this.held.values()) zoomDir += CAMERA_ZOOM[id] ?? 0;
    if (zoomDir) this.zoomAt(ZOOM_KEY_RATE ** (zoomDir * dt), undefined, 'zoom');
    let dx = 0, dy = 0;
    for (const id of this.held.values()) { const d = CAMERA_PAN[id]; if (d) { dx += d[0]; dy += d[1]; } }
    const n = Math.hypot(dx, dy) || 1;
    dx /= n; dy /= n;
    const [ex, ey] = this.edgePan.step(dt, this.edgePushNow());
    dx = Math.max(-1, Math.min(1, dx + ex)); dy = Math.max(-1, Math.min(1, dy + ey));
    if ((dx || dy) && !this.focused) {
      const speed = 720 / this.goal.zoom;
      this.anchor = undefined; this.easing = 'pan';
      this.goal.x += dx * speed * dt; this.goal.y += dy * speed * dt;
      this.clampGoal();
    }
    if (this.settled()) return;
    const reduced = useSettings.getState().reduceMotion || motionMs(1) === 0;
    // docs/42 CM4: the zoom eases on its own (a touch quicker under the fingers); the pan follows
    // fast, fastest under a drag
    const ease = (tc: number) => (reduced ? 1 : 1 - Math.exp(-dt / (this.slow ? EASE.slow : tc)));
    const kz = ease(this.easing === 'pinch' ? EASE.pinch : EASE.zoom), k = ease(EASE[this.easing]);
    const near = (a: number, b: number, eps: number) => Math.abs(a - b) < eps;
    this.cam.zoom = near(this.cam.zoom, this.goal.zoom, 0.002) ? this.goal.zoom : this.cam.zoom + (this.goal.zoom - this.cam.zoom) * kz;
    if (this.anchor) {
      // the anchored world point stays under the pointer through the whole ease
      this.cam.x = this.anchor.wx - this.anchor.sx / this.cam.zoom; this.cam.y = this.anchor.wy - this.anchor.sy / this.cam.zoom;
      if (this.cam.zoom === this.goal.zoom) { this.cam.x = this.goal.x; this.cam.y = this.goal.y; this.anchor = undefined; }
    } else {
      const eps = 0.25 / (this.cam.zoom * this.dpr);
      this.cam.x = near(this.cam.x, this.goal.x, eps) ? this.goal.x : this.cam.x + (this.goal.x - this.cam.x) * k;
      this.cam.y = near(this.cam.y, this.goal.y, eps) ? this.goal.y : this.cam.y + (this.goal.y - this.cam.y) * k;
    }
    this.applyCamera();
    if (this.cam.zoom === this.goal.zoom && this.drawnZoom !== this.cam.zoom) {
      // the zoom has settled: redraw the glyphs at it (pings and floating numbers just move)
      const f = this.cam.zoom / this.drawnZoom;
      for (const o of this.overlay.pings.list as unknown as Array<Phaser.GameObjects.Components.Transform>) { o.x *= f; o.y *= f; }
      this.drawnZoom = this.cam.zoom;
      this.applyCamera();
      this.redraw();
    }
  }

  /** docs/42 CM5: the push of a mouse resting in the edge band (none from over a control, under a modal, or with the setting off). */
  private edgePushNow(): [number, number] {
    const at = this.edgeAt;
    if (!at?.ok || this.press || this.pinch || this.focused || !useSettings.getState().edgePan || !this.scene.isActive() || document.hidden) return [0, 0];
    const push = edgePush(at.x, at.y, window.innerWidth, window.innerHeight, EDGE_BAND);
    if ((push[0] || push[1]) && document.querySelector('[aria-modal="true"]')) return [0, 0];
    return push;
  }

  /** Shows `cam`: the board camera at zoom × ratio, the overlay glyphs scaled from the zoom they were drawn at. */
  private applyCamera(): void {
    if (!this.snapshot) return;
    const z = this.cam.zoom, device = z * this.dpr;
    this.boardCam.setZoom(device);
    this.boardCam.setScroll(Math.round(this.cam.x * device) / device, Math.round(this.cam.y * device) / device);
    const ox = -Math.round(this.boardCam.scrollX * device) / this.dpr, oy = -Math.round(this.boardCam.scrollY * device) / this.dpr;
    const s = z / this.drawnZoom;
    for (const c of Object.values(this.overlay)) c.setPosition(ox, oy).setScale(s);
    useView.getState().setCamera(z, this.boardCam.scrollX, this.boardCam.scrollY);
  }

  /** docs/22 O4: the World's first named foe gets away instead of falling (the HUD publishes who and how). */
  private escapeOf(unitId: number): Escape | undefined {
    const n = useCinematic.getState().namedFoe;
    const u = this.units.get(unitId)?.unit;
    const encounterId = useEncounter.getState().encounterId;
    if (!u || !n || !isNamedFoe(n, encounterId, u)) return undefined;
    useCinematic.getState().markEscaped(n.encounterId);
    return n.escape;
  }

  /**
   * docs/22 O4: the face-off frames the leader — the camera eases in a step or two and centres the
   * tile in the board viewport; `undefined` eases back to where the player had it. Reduced motion
   * leaves the camera where it is.
   */
  private frame(tile: Tile | undefined, closer = 1.4): void {
    if (!this.snapshot) return;
    const reduced = useSettings.getState().reduceMotion || motionMs(1) === 0;
    const r = this.rect;
    if (tile && !reduced) {
      if (!this.focused) this.preFocus = { ...this.goal };
      this.focused = true;
      this.slow = true;
      this.anchor = undefined; this.easing = 'zoom';
      this.redraw();
      const z = stepZoom(this.goal.zoom * closer, 0, this.dpr);
      const p = this.mapper.tileCentre(tile);
      // centre the leader, but keep the board filling the viewport where it is big enough to (a leader at the east edge must not pull the void in)
      const clamp = (want: number, lo: number, size: number, view: number) => (size * this.cell >= view / z ? Math.max(-lo / z, Math.min(size * this.cell - (lo + view) / z, want)) : (size * this.cell) / 2 - (lo + view / 2) / z);
      this.goal = { zoom: z, x: clamp(p.x - (r.x + r.w / 2) / z, r.x, this.snapshot.board.w, r.w), y: clamp(p.y - (r.y + r.h / 2) / z, r.y, this.snapshot.board.h, r.h) };
    } else if (this.focused) {
      this.goal = this.preFocus ?? { zoom: this.goal.zoom, ...centredScroll(this.geom, this.goal.zoom, r) };
      this.preFocus = undefined;
      this.focused = false;
      this.time.delayedCall(reduced ? 0 : 700, () => { this.slow = false; });
      if (useView.getState().zoomGoal !== this.goal.zoom) useView.setState({ zoomGoal: this.goal.zoom, zoomAnchor: undefined });
      this.redraw();
    }
  }

  private setHovering(on: boolean): void {
    if (this.hovering === on) return;
    this.hovering = on;
    // with no hero selected, or while a pulse plays out, the cursor is drawn only while the pointer is on the board
    if (useAuthoring.getState().machineState.kind === 'Idle' || usePlayback.getState().playing) this.redraw();
  }

  private redraw(): void {
    const a = useAuthoring.getState();
    const v = useView.getState();
    const s = useSettings.getState();
    const e = useEncounter.getState();
    if (!this.snapshot) return;
    const playing = usePlayback.getState().playing;
    // During playback the glyphs describe the *next* pulse while the units still act out this one: hide them until the board catches up.
    // So does the gap before playback starts (the next pulse's snapshot is in, the board still shows
    // this one): what is drawn then is the plan just locked, which no longer holds.
    const stale = !a.projected || e.snapshot !== this.snapshot;
    const quiet = playing || this.focused || stale;
    this.overlay.intent.setVisible(!quiet); this.overlay.declarations.setVisible(!quiet); this.overlay.hud.setVisible(!quiet);
    // the ground marks too (reach washes, the selected hero's square: docs/145 put them under the
    // figures), all but the cursor, which keeps following the pointer through the pulse's playback so
    // the next turn opens where the mouse is
    this.layers.marks.setVisible(!this.focused);
    if (quiet && !this.focused) this.intent.quietCursor(this.hovering ? a.cursor : undefined, this.drawnZoom, this.cell);
    if (!a.projected || playing || stale) return;
    const view: IntentView = {
      zoom: this.drawnZoom, cell: this.cell,
      filter: v.filter, hidePaths: v.hidePaths || s.hideUnselectedPaths, humans: Math.max(1, new Set(Object.values(e.owners)).size),
      me: a.me, owners: e.owners, colourMode: s.colourMode, textScale: s.textSizeBp / 10000, highlightDanger: s.highlightDanger,
      dashPhase: this.dashPhase,
    };
    if (v.ownerHighlight) view.ownerHighlight = v.ownerHighlight;
    if (v.hoverHero !== undefined) view.hoverHero = v.hoverHero;
    if (a.selectedHero !== undefined) view.selectedHero = a.selectedHero;
    // with no hero selected the hovered square still lights, with what is on it
    const idle = a.machineState.kind === 'Idle';
    if (!idle || this.hovering) view.cursor = a.cursor;
    // docs/63: over a square never seen the cursor is only a frame: no route, chip or words
    if (unknownSquare(this.snapshot.fog, a.cursor)) view.dark = true;
    view.seats = playerSeats(this.snapshot.units, e.owners, e.colours);
    // docs/31: the hovered (else selected) hero's own sight reads clearest
    const focusHero = v.hoverHero ?? a.selectedHero;
    this.fog.setFocus(focusHero !== undefined ? this.snapshot.units.find((u) => u.heroId === focusHero)?.unitId : undefined);
    this.publishField(a.selectedHero);
    const reach = this.reachFor(a.machineState) ?? this.walkFor(a.machineState);
    if (reach) view.reach = reach;
    // docs/48 AR5: the coach line says how many the chosen action can reach from there
    useView.getState().setAimTargets(a.machineState.kind === 'ActionChosen' ? reach?.targets : undefined);
    if (a.suggestion && (!view.dark || a.suggestion.kind === 'plan') && ((idle && this.hovering) || a.machineState.kind === 'HeroSelected' || a.machineState.kind === 'SlotChosen')) view.suggest = a.suggestion;
    // docs/149 MP: the walks under way, drawn over the board whatever the cursor is doing
    if (a.routes.length) view.routes = a.routes;
    if (a.machineState.kind === 'ActionChosen' && !view.dark && a.probe && !a.probe.verdict.legal && tileEq(a.probe.tile, a.cursor)) view.note = a.probe.about ? `${a.probe.about} · ${reasonText(a.probe.verdict.reason)}` : reasonText(a.probe.verdict.reason);
    this.intent.redraw(a.projected, this.snapshot, view);
    // docs/46 T2: a foe the plan would drop stays standing until the pulse actually drops it
    for (const u of a.projected.units) this.units.get(u.unitId)?.setDimmed(false);
  }

  /**
   * docs/26: the fight's conditions and the selected (else first) hero's footing for the HUD,
   * from the same survey the Move overlay reads; asked once per snapshot and hero.
   */
  private publishField(selected: number | undefined): void {
    const bridge = getBridge();
    const a = useAuthoring.getState();
    if (!bridge || !a.snapshot || !this.snapshot) return;
    const heroes = this.snapshot.units.filter((u) => u.heroId !== undefined && u.onBoard !== false);
    const hero = heroes.find((u) => u.heroId === selected) ?? heroes[0];
    // keyed on the wasm handle the survey reads, not the drawn snapshot (they swap at different times)
    const key = `${a.snapshot.id}:${a.snapshot.hash}:${hero?.unitId ?? ''}`;
    if (this.fieldKey === key) return;
    this.fieldKey = key;
    const f = hero ? bridge.survey(a.snapshot, hero.unitId, 1) : undefined;
    const moves = f ? fieldMoves(f) : [];
    useView.getState().setField(f ? { conditions: f.conditions, traits: f.traits, sight: f.sight, modifiers: f.modifiers, windShift: f.windShift, ...(hero ? { hero: hero.name } : {}), ...(moves.length ? { moves } : {}) } : undefined);
  }

  /**
   * Mockup 05 + docs/48: while an action is chosen — or its hotbar tile is hovered with the hero
   * selected (AR4) — its range from where the hero will stand when that slot acts (AR1), and the
   * units or squares the sim's `legal()` says it may aim at, the same verdict the cursor chip shows.
   */
  private reachFor(st: ReturnType<typeof useAuthoring.getState>['machineState']): IntentView['reach'] {
    if (!this.snapshot) return undefined;
    if (st.kind === 'ActionChosen') return this.aimFor(st.heroId, st.slot, st.action, false);
    if (st.kind !== 'HeroSelected' && st.kind !== 'SlotChosen') return undefined;
    const id = useView.getState().hoverAction;
    const a = useAuthoring.getState();
    const def = id === undefined ? undefined : a.barActions().find((d) => d.id === id);
    if (!def || def.args === 'item' || def.id === 2) return undefined;
    if ([...a.rows.values()].some((r) => r.heroId === st.heroId && r.state === 'committed')) return undefined;
    const slot = a.slotFor(def.id);
    return slot === undefined ? undefined : this.aimFor(st.heroId, slot, def, true);
  }

  /** One action's range and targets for (hero, slot), asked once per plan and cached. */
  private aimFor(heroId: number, slot: Slot, action: ActionDef, hover: boolean): IntentView['reach'] {
    const a = useAuthoring.getState();
    const bridge = getBridge();
    const hero = this.snapshot?.units.find((u) => u.heroId === heroId);
    if (!bridge || !a.snapshot || !hero || !this.snapshot) return undefined;
    const key = `${heroId}:${slot}:${action.id}:${hover}:${a.projected?.planHash ?? ''}:${a.snapshot.id}:${a.snapshot.hash}`;
    if (this.reachCache?.key === key) return this.reachCache.reach;
    const tone = REACH_TONE[action.glyph] ?? 'attack';
    // docs/26: a Move asks the field once — every stopping square with its route and cost, and
    // the zones of control in the way — from the same walker `legal()` and the pulse use
    if (action.id === MOVE.id) {
      const field = slot === 'B' ? undefined : bridge.survey(a.snapshot, hero.unitId, slot);
      const reach = field ? { tiles: field.reach.filter((r) => r.stand).map((r) => r.tile), tone, field, ...(hover ? { quiet: true } : {}) } : undefined;
      this.reachCache = { key, reach };
      return reach;
    }
    // docs/48 AR1: the range from where the hero will stand when this slot acts
    const aim = action.args === 'interact' ? undefined : bridge.aim(a.snapshot, hero.unitId, slot, action.id);
    // an Interact draws no range, so where the hero will stand comes from the stands (docs/81 SA2)
    const from = aim?.from ?? bridge.stands(a.snapshot, hero.unitId, slot).find((x) => x.unit === hero.unitId)?.tile ?? hero.tile;
    const base = { heroId, unit: hero.unitId, slot, action: action.id, snapshotHash: this.snapshot.snapshotHash, revision: 0, substituted: false };
    const out: Tile[] = [];
    // docs/118 PT13: every candidate is judged in one batch (the plan runs once, not once per
    // candidate: per-square `legal` froze a big crawl for ~10 s when Misty Step was armed)
    const okAll = (args: Intent['args'][]) => bridge.legalMany(a.snapshot!, args.map((x) => ({ ...base, args: x }))).map((v) => v.legal);
    if (action.args === 'tile') {
      // ask `legal` inside the range only (it has the last word on sight for squares); a walk or
      // a teleport with no range drawn asks the squares it could land on, which the sim boxes
      const among = aim ? aim.tiles.map((t) => t.tile) : undefined;
      // docs/63: squares never seen take a click but are not lit (that would draw the rooms' outline)
      for (const t of bridge.legalTiles(a.snapshot, { ...base, args: { kind: 'tile', tile: from } }, among)) if (!tileEq(t, from) && !unknownSquare(this.snapshot.fog, t)) out.push(t);
    } else if (action.args === 'interact') {
      // docs/25: the stashes this hero could open from where it will stand
      // docs/126: a fallen foe's pouch is picked up by stepping on it, never opened
      const stashes = (this.snapshot.stashes ?? []).filter((f) => f.kind !== 'Pouch' && f.kind !== 'Find' && !f.hidden && !f.opened);
      // docs/41: and the mechanisms it could work from there, by the verb a click would use
      const works = (this.snapshot.fixtures ?? []).flatMap((f) => { const v = fixtureVerb(f, from, false, hero.unitId); return v ? [{ f, v }] : []; });
      const oks = okAll([...stashes.map((f) => ({ kind: 'interact' as const, fixture: f.id, verb: 'Open' as const })), ...works.map(({ f, v }) => ({ kind: 'interact' as const, fixture: f.id, verb: v }))]);
      [...stashes, ...works.map((x) => x.f)].forEach((f, i) => { if (oks[i]) out.push({ ...f.tile }); });
    } else if (action.args === 'unit' || action.args === 'unitDir') {
      // docs/81 SA3: each target where it will stand when this slot acts (a friend's planning ghost)
      const stands = new Map(bridge.stands(a.snapshot, hero.unitId, slot).map((x) => [x.unit, x.tile] as const));
      const units = this.snapshot.units.filter((u) => u.onBoard !== false);
      const oks = okAll(units.map((u) => (action.args === 'unit' ? { kind: 'unit' as const, unit: u.unitId } : { kind: 'unitDir' as const, unit: u.unitId, dir: 'E' as const })));
      units.forEach((u, i) => { if (oks[i]) out.push({ ...(stands.get(u.unitId) ?? u.tile), ...(u.size && u.size > 1 ? { size: u.size } : {}) }); });
    }
    const units = action.args === 'unit' || action.args === 'unitDir';
    const fog = this.snapshot.fog;
    const seenAim = aim && { ...aim, tiles: aim.tiles.filter((t) => !unknownSquare(fog, t.tile)) };
    const reach = { tiles: out, tone, ...(seenAim ? { aim: seenAim } : {}), from, ...(units ? { targets: out.length } : {}), ...(hover ? { quiet: true } : {}) };
    this.reachCache = { key, reach };
    return reach;
  }

  /**
   * docs/33 E2: with a hero selected and no action chosen, where it can walk with its next free
   * action — the field a click on open ground plans from (quiet, under the cursor's suggestion).
   */
  private walkFor(st: ReturnType<typeof useAuthoring.getState>['machineState']): IntentView['reach'] {
    if ((st.kind !== 'HeroSelected' && st.kind !== 'SlotChosen') || !this.snapshot) return undefined;
    const a = useAuthoring.getState();
    // a hero picked for the player at the turn's start shows its walk field once they ask (Move)
    if (a.autoSelected) return undefined;
    const bridge = getBridge();
    const hero = this.snapshot.units.find((u) => u.heroId === st.heroId);
    if (!bridge || !a.snapshot || !hero) return undefined;
    const filled = (sl: 1 | 2) => a.local.has(`${st.heroId}:${sl}`) || a.rows.has(`${st.heroId}:${sl}`);
    if ([...a.rows.values()].some((r) => r.heroId === st.heroId && r.state === 'committed')) return undefined;
    const slot = st.kind === 'SlotChosen' ? (st.slot === 'B' ? undefined : st.slot) : !filled(1) ? 1 : !filled(2) ? 2 : undefined;
    if (slot === undefined) return undefined;
    const key = `walk:${st.heroId}:${slot}:${a.projected?.planHash ?? ''}:${a.snapshot.id}:${a.snapshot.hash}`;
    if (this.reachCache?.key === key) return this.reachCache.reach;
    const field = bridge.survey(a.snapshot, hero.unitId, slot);
    const reach = field ? { tiles: field.reach.filter((r) => r.stand).map((r) => r.tile), tone: 'move' as const, field, quiet: true } : undefined;
    this.reachCache = { key, reach };
    return reach;
  }

  // ---- helpers ------------------------------------------------------------------------------------

  /**
   * Floating numbers (mockup 08): Cinzel 900 with a hard 2 px outline and no blur; they pop 12 px
   * over 220 ms in three steps so they move like sprites, hold, then blink out.
   */
  /** A number rising off a unit; `lift` rows above the usual spot, smaller (docs/29: a modifier's tag over the number it changed). */
  private floatText(t: Tile, text: string, colour: string, lift = 0): void {
    const p = this.mapper.tileCentre(t);
    const z = this.drawnZoom;
    const size = Math.round((lift ? 7 : 9) * Math.min(4, Math.max(2, z)));
    const sx = Math.round(p.x * z), sy = Math.round((p.y - this.cell / 2) * z) - lift * Math.round(17 * Math.min(4, Math.max(2, z)));
    const txt = oneShot(this, this.add.text(sx, sy, text, { fontFamily: 'Cinzel, Georgia, serif', fontSize: `${size}px`, fontStyle: '900', color: colour, stroke: '#0b0f1a', strokeThickness: 4 })).setOrigin(0.5, 1);
    txt.setResolution(this.dpr);
    this.overlay.pings.add(txt);
    this.tweens.chain({ targets: txt, tweens: [
      { y: sy - 12, duration: motionMs(220), ease: 'Stepped', easeParams: [3] },
      { alpha: 0, duration: motionMs(160), delay: motionMs(520), ease: 'Stepped', easeParams: [2] },
    ], onComplete: () => txt.destroy() });
  }

  /**
   * A card sliding through the middle of the board's viewport (screen-fixed: it ignores the camera).
   * docs/60: `depth` is the big arrival card of a crawl's level, `chamber` a small plate naming a
   * room, `danger` a red-edged call-out (a lair wakes, a roamer finds you, a fall).
   */
  private banner(text: string, ms: number, style: 'event' | 'depth' | 'chamber' | 'danger' | 'trap' = 'event', over?: string, foot?: string): void {
    const r = this.rect;
    const cx = r.x + r.w / 2;
    // docs/140: a trap's card is the depth card's size in the danger's colours, with its guise unmasked
    // under it; it sits low in the view, clear of the trap (and a foe's cry) the camera has centred
    const trap = style === 'trap';
    if (trap) style = 'depth';
    // a chamber's plate sits low in the view, clear of the call-outs that stack from the top
    let y = r.y + (trap ? r.h - Math.min(130, r.h * 0.2) : style === 'depth' ? Math.min(140, r.h * 0.22) : style === 'chamber' ? r.h - 80 : 56);
    const c = this.add.container(cx - r.w, y);
    // docs/50: a beat's card is "its name — what the players read", the line under the name
    const cut = text.indexOf(' — ');
    const head = cut < 0 ? text : text.slice(0, cut), line = cut < 0 ? '' : text.slice(cut + 3);
    const big = style === 'depth', small = style === 'chamber';
    const headSize = big ? 24 : small ? 12 : 15, lineSize = big ? 13 : small ? 10 : 11;
    const danger = style === 'danger' || trap;
    const edge = danger ? 0xd0574a : big ? 0xffd166 : 0xe0b24a;
    const wrap = Math.min(big ? 620 : 520, r.w - 48);
    const t = this.add.text(0, 0, head.toUpperCase(), { fontFamily: 'Cinzel, Georgia, serif', fontStyle: '700', fontSize: `${headSize}px`, color: danger ? '#ffb3a6' : '#ffd166', wordWrap: { width: wrap }, align: 'center' }).setOrigin(0.5).setResolution(this.dpr);
    const eyebrow = over ? this.add.text(0, 0, over.toUpperCase(), { fontFamily: 'Cinzel, Georgia, serif', fontStyle: '900', fontSize: trap ? '15px' : big ? '10px' : '9px', color: trap ? '#ff5a4f' : danger ? '#ff8a7a' : '#c9bfa8', letterSpacing: trap ? 3 : 1, stroke: trap ? '#1a0606' : undefined, strokeThickness: trap ? 3 : 0 }).setOrigin(0.5).setResolution(this.dpr) : undefined;
    const sub = line ? this.add.text(0, 0, line, { fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: `${lineSize}px`, color: '#f4ead2', wordWrap: { width: wrap }, align: 'center' }).setOrigin(0.5, 0.5).setResolution(this.dpr) : undefined;
    const under = foot ? this.add.text(0, 0, foot, { fontFamily: 'Georgia, serif', fontSize: '10px', color: '#c9bfa8', wordWrap: { width: wrap }, align: 'center' }).setOrigin(0.5, 0.5).setResolution(this.dpr) : undefined;
    const gap = big ? 8 : 4;
    const parts = [eyebrow, t, sub, under].filter((o): o is Phaser.GameObjects.Text => !!o);
    const total = parts.reduce((a, o) => a + o.height, 0) + gap * (parts.length - 1);
    let at = -total / 2;
    for (const o of parts) { o.setY(at + o.height / 2); at += o.height + gap; }
    const w = Math.max(small ? 200 : 280, Math.ceil(Math.max(...parts.map((o) => o.width))) + (big ? 96 : 64));
    const hh = Math.ceil(total / 2) + (big ? 16 : small ? 7 : 10);
    // cards that land together stack down from the top of the view, each under the last
    if (style !== 'depth' && style !== 'chamber') { y = r.y + 38 + this.stackBanner() + hh; c.setY(y); }
    const g = this.add.graphics();
    g.fillStyle(0x0b1020, big ? 0.9 : 0.94); g.fillRect(-w / 2, -hh, w, hh * 2); g.lineStyle(1, edge, 1); g.strokeRect(-w / 2 + 0.5, -hh + 0.5, w - 1, hh * 2 - 1);
    if (big) { g.lineStyle(1, edge, 0.5); g.strokeRect(-w / 2 + 4.5, -hh + 4.5, w - 9, hh * 2 - 9); }
    c.add([g, ...parts]);
    this.screenLayer.add(c);
    if (style !== 'depth' && style !== 'chamber') { this.bannerStack.push({ c, h: hh * 2 }); c.once(Phaser.GameObjects.Events.DESTROY, () => { this.bannerStack = this.bannerStack.filter((b) => b.c !== c); }); }
    this.liveBanners.push({ c, x: cx, w, h: hh * 2, until: this.time.now + ms * 0.85, fade: ms * 0.15 });
    c.once(Phaser.GameObjects.Events.DESTROY, () => { this.liveBanners = this.liveBanners.filter((b) => b.c !== c); });
    const reduced = useSettings.getState().reduceMotion || motionMs(1) === 0;
    if (trap) {
      // docs/140: the trap's card slams in — punched in from large, a red flash on its edge
      c.setPosition(cx, y).setAlpha(0).setScale(reduced ? 1 : 1.6);
      this.tweens.chain({ targets: c, tweens: [{ alpha: 1, scale: 1, duration: reduced ? 1 : Math.min(260, ms * 0.08), ease: 'Back.easeOut' }, { alpha: 1, duration: ms * 0.8 }, { alpha: 0, duration: ms * 0.12, onComplete: () => c.destroy() }] });
      return;
    }
    if (big) {
      // the depth card fades up in place rather than sliding: it is the moment of arrival
      c.setPosition(cx, y).setAlpha(0);
      this.tweens.chain({ targets: c, tweens: [{ alpha: 1, duration: reduced ? 1 : ms * 0.12 }, { alpha: 1, duration: ms * 0.73 }, { alpha: 0, duration: ms * 0.15, onComplete: () => c.destroy() }] });
      return;
    }
    this.tweens.chain({ targets: c, tweens: [{ x: cx, duration: ms * 0.15, ease: 'Quad.easeOut' }, { x: cx, duration: ms * 0.7 }, { alpha: 0, duration: ms * 0.15, onComplete: () => c.destroy() }] });
  }

  /** Call-outs that land together stack down the view instead of drawing over each other. */
  private bannerStack: Array<{ c: Phaser.GameObjects.Container; h: number }> = [];
  private stackBanner(): number { return this.bannerStack.reduce((a, b) => a + b.h + 6, 0); }

  /** Every card up, with where it rests, its size and when its fade starts (scene time). */
  private liveBanners: Array<{ c: Phaser.GameObjects.Container; x: number; w: number; h: number; until: number; fade: number }> = [];

  /**
   * A new board kills every tween, the cards' fades with them, so a card still up would never go:
   * settle each where it rests and fade it when its time is up.
   */
  private rearmBanners(): void {
    for (const b of this.liveBanners) {
      b.c.setX(b.x).setAlpha(1).setScale(1);
      this.tweens.chain({ targets: b.c, tweens: [{ alpha: 1, duration: Math.max(1, b.until - this.time.now) }, { alpha: 0, duration: Math.max(1, b.fade), onComplete: () => b.c.destroy() }] });
    }
  }

  /** Fade the topmost card under the screen point (CSS px) at once; whether there was one. */
  private dismissBannerAt(sx: number, sy: number): boolean {
    const b = [...this.liveBanners].reverse().find((o) => Math.abs(sx - o.c.x) <= o.w / 2 && Math.abs(sy - o.c.y) <= o.h / 2);
    if (!b) return false;
    this.liveBanners = this.liveBanners.filter((o) => o !== b);
    this.tweens.killTweensOf(b.c);
    // (no longer among the cards a new board re-arms: it goes with the tweens if they are killed)
    oneShot(this, b.c);
    this.tweens.add({ targets: b.c, alpha: 0, duration: 140, onComplete: () => b.c.destroy() });
    return true;
  }

  /** Stereo position of a unit's square across the board (docs/34): the same for every player. */
  private panOf(unit: number | undefined): number | undefined {
    if (unit === undefined || !this.snapshot) return undefined;
    const u = this.snapshot.units.find((x) => x.unitId === unit);
    return u ? panForTile(u.tile.x, this.snapshot.board.w) : undefined;
  }

  private async runPlayback(): Promise<void> {
    const pb = usePlayback.getState();
    if (!pb.plan) return;
    const plan = pb.plan;
    this.redraw();                                 // hides the glyph layers
    try {
      // docs/56 EP2: a card says so when the foes start acting
      const onStage = (i: number) => {
        usePlayback.getState().advance(i);
        if (i === foesFrom(plan) && motionMs(1) > 0) this.banner('The foes act', Math.max(900, 1500 / plan.speed));
      };
      const start = this.snapshot, end = useEncounter.getState().snapshot;
      const entries = [...new Set(plan.stages.flatMap((st) => st.groups.flatMap((g) => g.map((c) => c.entry))))].sort((a, b) => a.seq - b.seq);
      this.live = start && end && end !== start ? new LiveBoard(start, end, entries) : undefined;
      // where everyone will face once the pulse has played (or been skipped), for the next board
      if (start) this.facing = facingAfter(entries, start, this.facing);
      await this.player.play(plan, onStage, () => usePlayback.getState().skipRequested);
      // docs/56 EP3: the pulse settles — numbers fade, blood soaks away — before the next turn opens;
      // a pulse of nothing but steps with no foe acting has nothing to settle
      const skipped = usePlayback.getState().skipRequested;
      if (!onlyWalked(plan)) await this.player.wait(skipped ? 250 : SETTLE_MS, () => usePlayback.getState().skipRequested && !skipped);
    } finally {
      this.live = undefined;
      // Units snap back to their tiles; the controller then publishes the new board (→ setSnapshot).
      // (the fallen stay down: the new board draws them as bodies, docs/50)
      if (this.snapshot) for (const u of this.snapshot.units) { const s = this.units.get(u.unitId); if (s && !s.fallen) s.reset(u.tile, this.cell); }
      // docs/34: what follows the pulse — the stinger, the next pulse opening, the growls in the pause
      const cur = usePlayback.getState().current;
      if (cur) playAfterPlayback(cur.encounterId, cur.pulseIndex);
      usePlayback.getState().finish();
      this.redraw();
    }
  }

  /**
   * docs/79: a huge body lands — a short, small shake of the board, harder for a bigger one
   * (`thumpShake`); reduce motion keeps the board still.
   */
  private thump(size: number, unit?: UnitSprite): void {
    // docs/92 HM6: a commissioned body kicks up its ground as it lands (still under reduce motion: no particles)
    unit?.huge?.land();
    if (useSettings.getState().reduceMotion || motionMs(1) === 0) return;
    const s = thumpShake(size);
    if (!s) return;
    this.boardCam.shake(s.ms, s.intensity);
    this.thumps.push({ size, ...s });
  }

  /** docs/92 HM7: how much of a unit's sound rings in the huge bodies' hall. */
  private spaceOf(unit: number | undefined): number | undefined {
    if (unit === undefined) return undefined;
    const s = this.units.get(unit);
    return s?.huge ? hugeSpace(s.size) : undefined;
  }

  /** Which units have said they are bloodied this fight (docs/92 HM8: once each). */
  private readonly hugeBloodied = new Set<number>();
  /** The reveals played this scene (e2e reads them through `__fkHugeReveals`). */
  readonly hugeReveals: Array<{ unitId: number; kind: string; title: string }> = [];

  /**
   * docs/92 HM9: the first time the party sees a huge foe in a fight, the board stops to look at
   * it — the camera pushes in on it, the ground shakes, a braam and its roar, its name on a danger
   * card with a line over it, and its first words. Once a fight per browser.
   */
  private maybeReveal(s: UnitSprite): void {
    const art = s.huge;
    if (!art || s.unit.faction !== 'enemy' || s.unit.downed) return;
    const enc = useEncounter.getState().encounterId;
    if (enc === undefined || revealedBefore(enc, s.unit.unitId)) return;
    markRevealed(enc, s.unit.unitId);
    const kind = art.kind;
    const reduced = useSettings.getState().reduceMotion || motionMs(1) === 0;
    const space = hugeSpace(s.size);
    playSfx('cine.reveal', { gain: 1 });
    this.time.delayedCall(220, () => playSfx('huge.reveal', { space }));
    // docs/178: the deep's two come up with their water (the tide, the abyss) under the braam
    const rises = HUGE_REVEAL[kind];
    if (rises) this.time.delayedCall(380, () => playSfx(rises, { space, pan: this.panOf(s.unit.unitId) }));
    this.time.delayedCall(700, () => playSfx(HUGE_TAUNT[kind], { space, pan: this.panOf(s.unit.unitId) }));
    if (!reduced) {
      const centre = { x: s.tile.x + Math.floor(s.size / 2), y: s.tile.y + Math.floor(s.size / 2) };
      const cine = useCinematic.getState();
      if (!cine.faceoffPlaying && !cine.focus) {
        cine.setFocus(centre, 1.6);
        this.time.delayedCall(2800, () => { const c = useCinematic.getState(); if (c.focus?.x === centre.x && c.focus?.y === centre.y) c.setFocus(undefined); });
      }
      this.time.delayedCall(450, () => { this.boardCam.shake(700, s.size >= 3 ? 0.008 : 0.006); s.huge?.play('roar'); });
    }
    const title = hugeTitle(kind);
    this.banner(title.toUpperCase(), 3400, 'danger', hugeEyebrow(kind, [enc, s.unit.unitId]));
    this.hugeSay(s, 'reveal');
    this.hugeReveals.push({ unitId: s.unit.unitId, kind, title });
  }

  /** docs/92 HM6: a huge body's move plays its moment at the hero it most likely aims at, and it speaks. */
  private hugeMove(u: UnitSprite, name: string, ms: number): void {
    if (!u.huge) return;
    const target = this.nearestHero(u);
    u.huge.play(momentOf(u.huge.kind, name), target ? { x: target.x, y: target.y - this.cell * 0.5 } : undefined, ms);
    this.hugeSay(u, 'move');
  }

  private nearestHero(u: UnitSprite): UnitSprite | undefined {
    let best: UnitSprite | undefined, bd = Infinity;
    for (const s of this.units.values()) {
      if (s.unit.faction !== 'hero' || s.fallen || s.unit.downed) continue;
      const d = Math.hypot(s.x - u.x, s.y - u.y);
      if (d < bd) { bd = d; best = s; }
    }
    return best;
  }

  /** docs/92 HM8: the moments a huge foe speaks at, as a group of clips lands. */
  private hugeMoments(entries: ResolutionEntry[]): void {
    for (const en of entries) {
      const e = en.effect;
      if (e.kind === 'Damaged') {
        const s = this.units.get(e.unit);
        if (s?.huge && e.hpAfter > 0 && e.hpAfter * 2 <= s.unit.hpMax && !this.hugeBloodied.has(e.unit)) {
          this.hugeBloodied.add(e.unit);
          this.hugeSay(s, 'bloodied');
          s.huge.play('roar');
        }
      } else if (e.kind === 'Dropped') {
        const s = this.units.get(e.unit);
        if (s?.huge) { this.hugeSay(s, 'fall'); continue; }
        const by = e.by ?? en.actor;
        const k = by !== undefined ? this.units.get(by) : undefined;
        if (k?.huge && s?.unit.faction === 'hero') {
          this.hugeSay(k, 'triumph', s.unit.name);
          playSfx(HUGE_TAUNT[k.huge.kind], { space: hugeSpace(k.size), pan: this.panOf(k.unit.unitId) });
        }
      }
    }
  }

  private hugeSay(s: UnitSprite, say: HugeSay, about?: string): void {
    if (!s.huge || s.unit.appearance === undefined) return;
    const enc = useEncounter.getState().encounterId ?? 0;
    const pulse = usePlayback.getState().current?.pulseIndex ?? this.snapshot?.pulseIndex ?? 0;
    const kind = s.huge.kind;
    useHuge.getState().say({
      key: `${enc}:${s.unit.unitId}:${say}:${pulse}`, unitId: s.unit.unitId, name: s.unit.name, kind, appearance: s.unit.appearance,
      say, text: hugeLine(kind, say, [enc, s.unit.unitId, pulse], about), tile: { ...s.tile }, size: s.size,
    });
  }

  /** The landings that shook the board this scene (e2e reads them through `__fkThumps`). */
  private readonly thumps: Array<{ size: number; ms: number; intensity: number }> = [];

  /**
   * docs/56 EP5: the party's last hero is falling — the camera closes on it, the board shakes as it
   * hits the ground, the defeat sounds, and the defeat card goes up (the HUD holds the fight's end
   * there until the player continues).
   */
  private finale(unitId: number, ms: number): void {
    const u = this.units.get(unitId);
    const encounterId = useEncounter.getState().encounterId;
    if (encounterId !== undefined) useCinematic.getState().setDefeat(encounterId, u?.unit.name ?? 'The last hero');
    const reduced = useSettings.getState().reduceMotion || motionMs(1) === 0;
    playSfx('cine.defeat', { gain: 1 });
    if (!u || reduced) return;
    this.frame(u.tile, 2.2);
    this.time.delayedCall(ms * 0.55, () => this.boardCam.shake(420, 0.006));
  }
}

/** docs/47: spells thin with the effect-intensity setting, still under reduce motion, flash only when storms may. */
function spellQuality(s: { effectIntensityBp: number; reduceMotion: boolean; lightningFlashes: boolean }): SpellQuality {
  return { density: Math.max(0, Math.min(1, s.effectIntensityBp / 10_000)), still: s.reduceMotion, flashes: s.lightningFlashes && !s.reduceMotion };
}

/** The fog's look follows the lighting setting: particles in Full only, nothing moves under reduce motion. */
function fogMode(s: { lighting: string; reduceMotion: boolean }): { particles: boolean; still: boolean; strength: number } {
  return { particles: s.lighting === 'full' && !s.reduceMotion, still: s.reduceMotion, strength: s.lighting === 'soft' ? 0.85 : 1 };
}

const onBoard = (s: Snapshot): number[] => s.units.filter((u) => u.onBoard !== false).map((u) => u.unitId);

/** `?time=day|night` draws the fight in its set's day or night art whatever the hour (docs/166 C1). */
function timeOverride(): 'day' | 'night' | undefined {
  try { const v = new URLSearchParams(window.location.search).get('time'); return v === 'day' || v === 'night' ? v : undefined; } catch { return undefined; }
}

/** docs/166 C1: the World's biome: `?biome=`, the board's own (once the forge sends it), or the World clock's. */
function worldBiome(board: Snapshot['board']): string | undefined {
  try { const v = new URLSearchParams(window.location.search).get('biome'); if (v) return v; } catch { /* no window */ }
  const own = (board as { biome?: string }).biome;
  if (own) return own;
  const worldId = useSession.getState().worldId;
  if (worldId === undefined) return undefined;
  return getConn()?.db.worldClock.worldId.find(worldId)?.biome || undefined;
}

/** `?hour=H` pins the time of day for art review (docs/19). */
function hourOverride(): number | undefined {
  try { const v = new URLSearchParams(window.location.search).get('hour'); return v === null || v === '' || !Number.isFinite(Number(v)) ? undefined : Number(v); } catch { return undefined; }
}

/** `?season=winter` pins the season for art review (docs/23). */
function seasonOverride(): ReturnType<typeof parseSeason> {
  try { return parseSeason(new URLSearchParams(window.location.search).get('season')); } catch { return undefined; }
}

/** `?weather=rain[:intensity[:wind]]` pins the weather for art review (docs/20). */
function weatherOverride(): ReturnType<typeof parseWeather> {
  try { return parseWeather(new URLSearchParams(window.location.search).get('weather')); } catch { return undefined; }
}
